import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './Project_admin.css';
import { add_serial, delete_projects_permission, delete_projects_relation, delete_project_pins, get_brevo_folders, get_brevo_lists, get_project_opportunities, get_project_permissions, get_project_pins, get_project_pins_dashboard_status, get_project_serials, get_project_updt_status, insert_project_pins_batches, insert_projects_permission, link_device_user, request_brevo_csv, resolve_user_by_email, refresh_ies_enrichment, sync_project_pins_batch, sync_project_pins_to_bigquery, update_project, update_project_link_external, update_projects_permission, update_serial_sub_cat, validate_procces} from '../../utils/services';
import { list_ies, set_serial_ies, set_pin_ies } from '../../utils/ies_services';
import { pinMetaService, pinAssignService } from '../../utils/pins_services';
import { extractPinsFromCommaInput, extractPinsFromCsvOrList } from '../../utils/parseProjectPins';
import {  useNavigate, useSearchParams } from 'react-router-dom';
import Modal_proyectos from '../menus/Modal';
import ProjectMembers from './project_members/ProjectMembers';
import md5 from 'md5';
import Select from 'react-select';
import { loadCountriesData, buildCountryNameOptions, buildCountryIndex } from '../../utils/countries';

// Cache-bust del iframe del lab-dashboard. Se calcula UNA vez por carga completa de la página
// del admin. El lab-dashboard se despliega aparte y su index.html se sirve cacheable → el iframe
// (cross-origin) conserva un build viejo aun con cmd+shift+r del padre (el hard-reload no propaga
// al subframe cross-origin). Con ?v= atado a este valor, cualquier reload completo genera una URL
// nueva → el iframe revalida su index.html y trae el build más reciente. Dentro de la misma sesión
// el valor es estable (los assets hasheados siguen cacheando normal).
const IFRAME_CACHE_BUST = Date.now();

function projectPinRowKey(row) {
  if (row?.dbId != null) return `id:${row.dbId}`;
  return `local:${String(row.pin)}`;
}

function mapServerPinRowToState(row) {
  return {
    dbId: row.id,
    pin: row.pin,
    email: row.user_email || '',
    userName: row.user_name || '',
    userId: row.user_id ?? null,
    pin_type_id: row.pin_type_id ?? null,
    time_type_id: row.time_type_id ?? null,
    status_type_id: row.status_type_id ?? null,
    serial_id: row.serial_id ?? null,
    serial_identify: row.serial_identify || '',
    actived_at: row.actived_at || null,
    mpin_created_at: row.created_at || null,
    mpin_updated_at: row.updated_at || null,
    synced_at: row.synced_at || null,
    ies_id: row.ies_id ? Number(row.ies_id) : 0,
    ies_name: row.ies_name || '',
    ies_country: row.ies_country || '',
    ies_city: row.ies_city || '',
  };
}

function formatPinTableDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}

function escapeCsvField(value) {
  const s = value == null ? '' : String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function rowToPinCsvCells(row, pinMetaMaps) {
  const status =
    pinMetaMaps.statusMap.get(String(row.status_type_id ?? ''))
    || (row.status_type_id != null ? row.status_type_id : '—');
  const timeType =
    pinMetaMaps.timeMap.get(String(row.time_type_id ?? ''))
    || (row.time_type_id != null ? row.time_type_id : '—');
  const pinType =
    pinMetaMaps.pinTypeMap.get(String(row.pin_type_id ?? ''))
    || (row.pin_type_id != null ? row.pin_type_id : '—');
  return [
    row.pin ?? '',
    row.ies_name || '—',
    row.email || '—',
    row.userName || '—',
    status,
    row.serial_identify || '—',
    timeType,
    pinType,
    row.userId != null ? row.userId : '—',
    formatPinTableDate(row.actived_at),
    formatPinTableDate(row.mpin_created_at),
  ];
}

const PROJECT_PINS_CSV_HEADERS = [
  'PIN',
  'Institución',
  'Email',
  'Nombre',
  'Estado',
  'Serial',
  'Vigencia',
  'Tipo PIN',
  'User ID',
  'Activación',
  'Creado (mpins)',
];

function buildProjectPinsCsv(rows, pinMetaMaps) {
  const lines = [
    PROJECT_PINS_CSV_HEADERS.map(escapeCsvField).join(','),
    ...rows.map((row) => rowToPinCsvCells(row, pinMetaMaps).map(escapeCsvField).join(',')),
  ];
  return lines.join('\r\n');
}

function formatProjectPinsCsvFilename(projectId) {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const ts = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}`;
  const pid = projectId != null && String(projectId).trim() !== '' ? String(projectId) : 'proyecto';
  return `pines_proyecto_${pid}_${ts}.csv`;
}

function downloadTextAsFile(filename, content, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob(['\uFEFF', content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * get_project_serials devuelve JSON tipo { "0": { …fila projects… }, "serials": [...] }.
 * Normaliza a la fila del proyecto (no siempre coincide membersList[0] según motor/parseo).
 */
function getProjectRowFromSerialsResponse(membersList) {
  if (!membersList || typeof membersList !== 'object' || Array.isArray(membersList)) return null;
  const tryRow = (row) => (row && typeof row === 'object' && !Array.isArray(row) ? row : null);
  const direct = tryRow(membersList[0] ?? membersList['0']);
  if (direct) return direct;
  const keys = Object.keys(membersList).filter((k) => k !== 'serials');
  const numKey = keys.find((k) => /^\d+$/.test(String(k)));
  if (numKey != null) return tryRow(membersList[numKey]);
  return null;
}

/** 0 = pines internos, 1 = lista Brevo (columna projects.link_external) */
function parseLinkExternalFromProjectRow(projectRow) {
  if (!projectRow || typeof projectRow !== 'object') return 1;
  const raw = projectRow.link_external ?? projectRow.linkExternal;
  if (raw === 0 || raw === '0' || raw === false) return 0;
  if (raw === undefined || raw === null || raw === '') return 1;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 1;
  return n === 0 ? 0 : 1;
}

function isProjectAdminInternalUser(usrType) {
  const s = String(usrType ?? '').trim();
  return s !== '' && parseInt(s, 10) === 0;
}

/** project_management 1 = admin total, 2 = acceso por proyecto (miembro puede invitar) */
function hasProjectManagementAccess(permission) {
  const p = String(permission ?? '').trim();
  return p === '1' || p === '2';
}

/**
 * Celda "Sub_cat" editable de la tabla de seriales: muestra el sub_cat actual y
 * permite editarlo inline (projects_relations.sub_cat). Vacío es válido (lo limpia).
 */
function SerialSubCatCell({ serial, token, projectId, canEdit, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(serial.sub_cat || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  const start = () => {
    setValue(serial.sub_cat || '');
    setError(false);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setError(false);
  };
  const save = async () => {
    const next = value.trim();
    if (next === (serial.sub_cat || '')) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setError(false);
    const res = await update_serial_sub_cat(token, serial.id, projectId, next);
    setBusy(false);
    if (res === 'OK') {
      onSaved(serial.id, next);
      setEditing(false);
    } else {
      setError(true);
    }
  };

  return (
    <div className="serial-link-cell">
      {!editing ? (
        <div className="serial-link-summary">
          {serial.sub_cat ? (
            <span className="serial-link-user" title={serial.sub_cat}>
              {serial.sub_cat}
            </span>
          ) : (
            <span className="serial-link-none">Sin sub_cat</span>
          )}
          {canEdit ? (
            <button type="button" className="serial-link-btn" onClick={start}>
              Editar
            </button>
          ) : null}
        </div>
      ) : (
        <div className="serial-link-summary">
          <input
            type="text"
            className="serial-search-input serial-link-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
              else if (e.key === 'Escape') cancel();
            }}
            disabled={busy}
            autoFocus
          />
          <button type="button" className="serial-link-btn" onClick={save} disabled={busy}>
            {busy ? '…' : 'Guardar'}
          </button>
          <button type="button" className="serial-link-btn" onClick={cancel} disabled={busy}>
            Cancelar
          </button>
        </div>
      )}
      {error ? <p className="serial-link-error">No se pudo guardar el sub_cat.</p> : null}
    </div>
  );
}

/**
 * Celda "Usuario vinculado" de la tabla de seriales: muestra el usuario TOMi
 * vinculado al dispositivo (o "Sin vincular") y permite buscar un usuario de la
 * plataforma por email (musers) y vincularlo. Respeta el cooldown de 10 días que
 * aplica el backend.
 */
function SerialUserLinkCell({ serial, token, projectId, canEdit = true, onLinked }) {
  const [open, setOpen] = useState(false);
  const [emailQuery, setEmailQuery] = useState('');
  const [emailResults, setEmailResults] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'error'|'cooldown', text }

  const linked = Number(serial.usr_id_tomi) > 0;
  const linkedLabel = linked
    ? serial.linked_name || serial.linked_email || `Usuario #${serial.usr_id_tomi}`
    : '';

  const reset = () => {
    setOpen(false);
    setEmailQuery('');
    setEmailResults(null);
    setMessage(null);
  };

  const doLink = async (user) => {
    if (!canEdit) return;
    setBusy(true);
    setMessage(null);
    const res = await link_device_user(token, projectId, serial.serial, user.user_id);
    setBusy(false);
    if (res === 'INVALID' || res == null) {
      setMessage({ type: 'error', text: 'No se pudo vincular (sesión o permiso).' });
      return;
    }
    if (res.ok) {
      onLinked(serial.id, {
        user_id: user.user_id,
        user_name: user.user_name,
        user_email: user.user_email,
        last_usr_id_change: res.data ? res.data.last_usr_id_change : null,
      });
      reset();
      return;
    }
    const err = res.error || {};
    if (err.code === 'COOLDOWN_ACTIVE') {
      setMessage({
        type: 'cooldown',
        text: `Este TOMi8 cambió de usuario hace poco; podrás recambiarlo el ${err.next_allowed_date}.`,
      });
      return;
    }
    setMessage({ type: 'error', text: err.message || 'No se pudo vincular el dispositivo.' });
  };

  const searchEmail = async () => {
    if (!canEdit) return;
    const email = emailQuery.trim();
    if (!email) return;
    setEmailBusy(true);
    setEmailResults(null);
    const res = await resolve_user_by_email(token, projectId, email);
    setEmailBusy(false);
    const users = res && Array.isArray(res.users) ? res.users : [];
    setEmailResults(users);
  };

  return (
    <div className="serial-link-cell">
      <div className="serial-link-summary">
        {linked ? (
          <span className="serial-link-user" title={serial.linked_email || ''}>
            {linkedLabel}
          </span>
        ) : (
          <span className="serial-link-none">Sin vincular</span>
        )}
        {canEdit ? (
          <button
            type="button"
            className="serial-link-btn"
            onClick={() => (open ? reset() : setOpen(true))}
          >
            {open ? 'Cerrar' : linked ? 'Cambiar' : 'Vincular'}
          </button>
        ) : null}
      </div>

      {canEdit && open ? (
        <div className="serial-link-picker">
          <div className="serial-link-email">
            <span className="serial-link-email-label">Buscar usuario por email</span>
            <div className="serial-link-email-row">
              <input
                type="email"
                className="serial-search-input serial-link-input"
                placeholder="email@dominio.com"
                value={emailQuery}
                onChange={(e) => setEmailQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') searchEmail(); }}
                autoFocus
              />
              <button type="button" className="serial-link-btn" disabled={emailBusy} onClick={searchEmail}>
                {emailBusy ? '...' : 'Buscar'}
              </button>
            </div>
            {emailResults && emailResults.length === 0 && (
              <p className="serial-link-empty">No se encontró un usuario con ese email.</p>
            )}
            {emailResults && emailResults.length > 0 && (
              <ul className="serial-link-results">
                {emailResults.map((u) => (
                  <li key={u.user_id}>
                    <button type="button" className="serial-link-option" disabled={busy} onClick={() => doLink(u)}>
                      <span className="serial-link-option-name">{u.user_name || `Usuario #${u.user_id}`}</span>
                      <span className="serial-link-option-email">{u.user_email}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {message && (
            <p className={message.type === 'cooldown' ? 'serial-link-cooldown' : 'serial-link-error'}>
              {message.text}
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Celda "Institución" del modal Lista de seriales: muestra la IE vinculada
 * (ies_serials) y abre el picker para cambiar/vincular.
 */
function SerialIesCell({ serial, canEdit = true, onRequestPick }) {
  const linked = Number(serial.ies_id) > 0;
  const label = linked
    ? serial.ies_name || `IE #${serial.ies_id}`
    : '';
  const title = linked
    ? [serial.ies_name, serial.ies_city, serial.ies_country].filter(Boolean).join(' · ')
    : '';

  return (
    <div className="serial-link-cell">
      <div className="serial-link-summary">
        {linked ? (
          <span className="serial-link-user" title={title}>
            {label}
          </span>
        ) : (
          <span className="serial-link-none">Sin IE</span>
        )}
        {canEdit ? (
          <button
            type="button"
            className="serial-link-btn"
            onClick={() => onRequestPick && onRequestPick(serial)}
          >
            {linked ? 'Cambiar' : 'Vincular'}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Modal para buscar y seleccionar una institución educativa (nombre / país / ciudad).
 */
function IesPickerModal({ isOpen, onClose, token, onSelect, countryIndex }) {
  const navigate = useNavigate();
  const [iesList, setIesList] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const resolveCountryLabel = useCallback(
    (countryCode) => {
      if (!countryCode) return '';
      const upper = String(countryCode).trim().toUpperCase();
      if (countryIndex?.byCca3?.[upper]) {
        return countryIndex.byCca3[upper].name;
      }
      return countryCode;
    },
    [countryIndex]
  );

  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setSaving(false);
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      const result = await list_ies(token);
      if (cancelled) return;
      if (result === 'INVALID') {
        navigate('/');
        return;
      }
      setIesList(Array.isArray(result) ? result : []);
      setLoading(false);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, token, navigate]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return iesList;
    return iesList.filter((ie) => {
      const name = String(ie.name || '').toLowerCase();
      const city = String(ie.city || '').toLowerCase();
      const country = resolveCountryLabel(ie.country).toLowerCase();
      const countryRaw = String(ie.country || '').toLowerCase();
      return name.includes(q) || city.includes(q) || country.includes(q) || countryRaw.includes(q);
    });
  }, [iesList, query, resolveCountryLabel]);

  const handlePick = async (ie) => {
    if (saving) return;
    setSaving(true);
    await onSelect(ie);
    setSaving(false);
  };

  const handleClear = async () => {
    if (saving) return;
    setSaving(true);
    await onSelect(null);
    setSaving(false);
  };

  return (
    <Modal_proyectos isOpen={isOpen} onClose={onClose} title="Seleccionar institución">
      <div className="ies-picker-modal">
        <input
          type="search"
          className="serial-search-input ies-picker-search"
          placeholder="Buscar por nombre, país o ciudad…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />
        <div className="ies-picker-actions">
          <button
            type="button"
            className="serial-link-btn"
            disabled={saving}
            onClick={handleClear}
          >
            Quitar vinculación
          </button>
        </div>
        {loading ? (
          <p className="serial-link-empty">Cargando instituciones…</p>
        ) : (
          <ul className="serial-link-results ies-picker-results">
            {filtered.length === 0 ? (
              <li>
                <p className="serial-link-empty">No hay resultados.</p>
              </li>
            ) : (
              filtered.map((ie) => (
                <li key={ie.id}>
                  <button
                    type="button"
                    className="serial-link-option"
                    disabled={saving}
                    onClick={() => handlePick(ie)}
                  >
                    <span className="serial-link-option-name">{ie.name}</span>
                    <span className="serial-link-option-email">
                      {[ie.city, resolveCountryLabel(ie.country)].filter(Boolean).join(' · ')}
                    </span>
                  </button>
                </li>
              ))
            )}
          </ul>
        )}
      </div>
    </Modal_proyectos>
  );
}

function Project_admin  ({usr_id, permission, usr_type, pin_management}) {

  const serialInputRef = useRef(null);
  const initialProjectValuesRef = useRef({ nombre_proy: '', country: '', licensed_until: null });
  const token = localStorage.getItem("token");
  const navigate = useNavigate();

  const [currentfolderName, setcurrentfolderName] = useState('');
  const [num_seriales, set_num_serials] = useState(0);
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id"); // Obtiene el valor de 'id'
  // Pista de mes al abrir desde la North Star ('1' = mes anterior/cerrado, '0' = actual).
  // El lab abre en el mismo mes que mostraba el widget.
  const nsMonthOffset = searchParams.get("d_nsMonthOffset") || searchParams.get("nsMonthOffset");
  // Serial al abrir desde el helpdesk (?d_serial=T1TB000561): el lab abre directo en
  // "Uso TOMi" filtrado a ese TOMi (modo dispositivo). Suele venir con id=104 (Proyecto
  // Global, que agrega todo el parque, así cualquier serial existe).
  const serial = searchParams.get("d_serial") || searchParams.get("serial");
  const [nombre_proy, setnombre_proy] = useState('');
  const [country, setcountry] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const [newEmail, setnewMail] = useState('');
  

  const [inProgress, setInProgress] = useState(false);
  const [link_name, setlink_name] = useState('');
  const [link_id, setlink_id] = useState('');
  const [link_token, setlink_token] = useState('');
  /** 1 = lista Brevo, 0 = pines internos (projects.link_external) */
  const [linkExternal, setLinkExternal] = useState(1);
  const [BrevoElements, setBrevoElements] = useState('');
  const [CloudElements, setCloudElements] = useState('');
  /** Total de filas en project_pins (solo modo lista interna de pines) */
  const [pinsListTotalCount, setPinsListTotalCount] = useState(null);
  /**
   * Pines REDIMIDOS del proyecto. El badge "Lista Pines (x/y)" mostraba en la x el
   * COUNT de users_by_project —personas en el roster del dashboard— contra un total
   * de PINES: dos unidades distintas en una misma fracción, que se leía como "26 de
   * 30 pines usados" sin serlo. Desde que se enrolan los vinculados por TOMi la x
   * puede incluso superar a la y (5 pines, 20 maestros por dispositivo → "20/5").
   * Cuántos maestros ve el dashboard ahora se responde en la pestaña Maestros.
   */
  const [pinsRedeemedCount, setPinsRedeemedCount] = useState(null);
  const [countryOptions, setCountryOptions] = useState([]);

  const [ok_serials, set_ok_serials] = useState('');
  const [failed_serials, set_failed_serials] = useState('');
  const [num_seriales_procesados, set_procesados] = useState(0);

  let seriales;

  //LIST OF SERIAL MEMBERS:
  const [SerialList, setSerialList] = useState([]);
  const [folderPage, setFolderPage] = useState(true);

  const [permissions_users, setpermissions_users] = useState([]);
  const [project_opportunities, setProject_opportunities] = useState([]);
  const [licensed_until, setLicensedUntil] = useState(null);
  /** Rol del viewer en projects_permission (0/1), null si no hay fila. */
  const [viewerPermission, setViewerPermission] = useState(null);
  const canEditLicensedUntil = isProjectAdminInternalUser(usr_type);
  const canToggleProjectAdmin = isProjectAdminInternalUser(usr_type);
  const [isEditingLicensedUntil, setIsEditingLicensedUntil] = useState(false);
  const [entryErrorMessage, setEntryErrorMessage] = useState('');

  const [isModalOpen, setModalOpen] = useState(false);
  const [isModalPermisos, setModalPermisos] = useState(false);
  const [isModalListaSerialesOpen, setModalListaSerialesOpen] = useState(false);
  const [serialListView, setSerialListView] = useState('list'); // 'list' | 'add'
  const [searchSerial, setSearchSerial] = useState('');
  const [searchSubCat, setSearchSubCat] = useState('');
  const [selectedSerialIds, setSelectedSerialIds] = useState([]);
  const serialSelectAllRef = useRef(null);
  const [isIesPickerOpen, setIesPickerOpen] = useState(false);
  /** 'serial' | 'pin' */
  const [iesPickerKind, setIesPickerKind] = useState('serial');
  const [iesPickerItems, setIesPickerItems] = useState([]);
  const [iesCountryIndex, setIesCountryIndex] = useState(null);
  const [folders_brevo, setfolders_brevo] = useState([]);
  const [projects_brevo, setprojects_brevo] = useState([]);
  const [brevoModalTab, setBrevoModalTab] = useState('pines');
  const [vinculoPines, setVinculoPines] = useState([]);
  const [pinInput, setPinInput] = useState('');
  const [pendingCsvPins, setPendingCsvPins] = useState([]);
  const [searchPin, setSearchPin] = useState('');
  const [pinsSubmitting, setPinsSubmitting] = useState(false);
  const [pinsLoading, setPinsLoading] = useState(false);
  const [pinsDeleting, setPinsDeleting] = useState(false);
  const [selectedPinKeys, setSelectedPinKeys] = useState([]);
  const pinSelectAllRef = useRef(null);
  const csvFileInputRef = useRef(null);
  const [pinsMeta, setPinsMeta] = useState({ statusTypes: [], timeTypes: [], pinTypes: [] });
  const [showPinsSyncWaitModal, setShowPinsSyncWaitModal] = useState(false);
  const [pinsSyncRunning, setPinsSyncRunning] = useState(false);
  /** null | 'batch' | 'bigquery' — pasos del flujo pines + dashboard */
  const [pinsSyncStep, setPinsSyncStep] = useState(null);
  const [pinAssignModal, setPinAssignModal] = useState(null);
  const [pinAssignNewEmail, setPinAssignNewEmail] = useState('');
  const [pinAssignConfirmEmail, setPinAssignConfirmEmail] = useState('');
  const [pinAssignSubmitting, setPinAssignSubmitting] = useState(false);
  const [pinsSort, setPinsSort] = useState({ key: 'pin', direction: 'asc' });

  useEffect(() => {
    loadCountriesData()
      .then((data) => {
        setCountryOptions(buildCountryNameOptions(data));
        setIesCountryIndex(buildCountryIndex(data));
      })
      .catch(() => {
        setCountryOptions([]);
        setIesCountryIndex(null);
      });
  }, []);

  const getCountryOptionByValue = (value) => {
    if (!value) return null;
    return countryOptions.find((opt) => opt.value === value) || null;
  };

  const formatCountryOptionLabel = (option) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      {option.flagUrl ? (
        <img
          src={option.flagUrl}
          alt=""
          style={{ width: 18, height: 12, objectFit: 'cover', borderRadius: 2 }}
        />
      ) : null}
      <span>{option.label}</span>
    </div>
  );

  // ---- Dashboard del proyecto (lab-dashboard-frontend embebido) ----
  // Handoff de sesión: el lab espera el token vía postMessage en su ruta /auth
  // (orígenes permitidos incluyen localhost:3000). Configurable por REACT_APP_LAB_DASHBOARD_URL.
  const LAB_DASHBOARD_URL = process.env.REACT_APP_LAB_DASHBOARD_URL || 'http://localhost:5284/dashboards';
  const labDashboardOrigin = useMemo(() => {
    try { return new URL(LAB_DASHBOARD_URL).origin; } catch { return '*'; }
  }, [LAB_DASHBOARD_URL]);

  // Parámetros que esta página REENVÍA al dashboard: todo lo que llegue con el prefijo
  // `d_`, sin el prefijo. `?id=99&d_page=uso-tomi&d_app=whatsapp` le llega al iframe
  // como `?page=uso-tomi&app=whatsapp`.
  //
  // El prefijo existe para no tener que adivinar de quién es cada parámetro. Reenviar
  // todo lo que llegue obligaría a mantener una lista negra de los del admin (`id`,
  // `tab`, lo que se añada mañana) y esas listas se olvidan de actualizar justo el día
  // que importa — con la fuga yendo hacia un iframe, además. La regla así cabe en una
  // línea y no envejece: lo que lleva `d_` es del iframe, lo demás es mío.
  //
  // Y sobre todo: añadir un parámetro nuevo YA NO TOCA ESTE ARCHIVO. Antes cada uno
  // (`serial`, `nsMonthOffset`) era un caso especial con su propio camino, y abrir uno
  // nuevo costaba un despliegue del admin. Ahora se acuerdan quien arma el enlace y el
  // dashboard; el admin solo transporta.
  const dashParams = useMemo(() => {
    const PREFIX = 'd_';
    const out = new URLSearchParams();
    for (const [k, v] of searchParams.entries()) {
      if (k.startsWith(PREFIX) && k.length > PREFIX.length) out.set(k.slice(PREFIX.length), v);
    }
    // Compat con los enlaces sin prefijo que ya están repartidos por chat y correo.
    // Se aceptan pero no se generan: los dos generadores viven en este repo y ya emiten
    // `d_` (helpdesk/Email_info.js y NorthStar.js).
    if (!out.has('serial') && searchParams.get('serial')) {
      out.set('serial', searchParams.get('serial'));
    }
    if (!out.has('nsMonthOffset') && searchParams.get('nsMonthOffset')) {
      out.set('nsMonthOffset', searchParams.get('nsMonthOffset'));
    }
    return out.toString();
  }, [searchParams]);

  // Nonce que fuerza el remonte del iframe del dashboard (entra en su `key`).
  // reloadDashboard() se llama al COMPLETAR mutaciones que el dashboard refleja
  // (sync/alta/baja/reasignación de pines, vínculo IE, lista Brevo, alta/baja de
  // TOMis) — nunca en cargas de solo-lectura, para no recargar de gratis.
  const [dashboardReloadNonce, setDashboardReloadNonce] = useState(0);
  const reloadDashboard = () => setDashboardReloadNonce((n) => n + 1);

  // Cuando el iframe del dashboard carga, le pasamos la sesión (token) del admin por postMessage (push inicial).
  const handleDashboardIframeLoad = (e) => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('[Dashboard] No hay token en localStorage para compartir con el dashboard.');
      return;
    }
    const payload = {
      type: 'LAB_SESSION_TOKEN',
      token,
      projectId: String(parseInt(id, 10)),
      projectName: nombre_proy,
      openMonthOffset: nsMonthOffset,
    };
    try {
      e.target.contentWindow.postMessage(payload, labDashboardOrigin);
    } catch (err) {
      console.warn('[Dashboard] No se pudo enviar la sesión al dashboard', err);
    }
  };

  // Handshake pull: el lab puede PEDIR un token fresco (al montar o ante un 401)
  // enviando { type: 'LAB_REQUEST_TOKEN' }. Respondemos con el token ACTUAL del
  // admin, solo al origen pineado del lab (validado contra labDashboardOrigin).
  // Así, mientras el admin tenga sesión válida, el dashboard nunca se queda con un
  // token vencido — y no se expone el token a orígenes no confiables.
  useEffect(() => {
    const handleLabTokenRequest = (event) => {
      if (event.origin !== labDashboardOrigin) return;
      if (!event.data || event.data.type !== 'LAB_REQUEST_TOKEN') return;
      const token = localStorage.getItem('token');
      const payload = {
        type: 'LAB_SESSION_TOKEN',
        token: token || null,
        projectId: String(parseInt(id, 10)),
        projectName: nombre_proy,
        openMonthOffset: nsMonthOffset,
      };
      try {
        (event.source || window).postMessage(payload, event.origin);
      } catch (err) {
        console.warn('[Dashboard] No se pudo responder la solicitud de token', err);
      }
    };
    window.addEventListener('message', handleLabTokenRequest);
    return () => window.removeEventListener('message', handleLabTokenRequest);
  }, [id, nombre_proy, labDashboardOrigin, nsMonthOffset]);

  // El dashboard embebido va en un iframe sandboxed: Chrome bloquea su descarga del
  // CSV (pese a allow-downloads) porque el export hace `await fetchAll()` y se pierde
  // el gesto de usuario que exige una descarga en sandbox. El lab nos envía el CSV
  // ({ type: 'LAB_DOWNLOAD_CSV', filename, csv }) y lo descargamos desde aquí (admin
  // top-level, NO sandboxed), donde la descarga no tiene esa traba. Solo aceptamos el
  // mensaje del origen pineado del lab.
  useEffect(() => {
    const handleLabDownload = (event) => {
      if (event.origin !== labDashboardOrigin) return;
      const d = event.data;
      if (!d || d.type !== 'LAB_DOWNLOAD_CSV' || typeof d.csv !== 'string') return;
      try {
        const raw = typeof d.filename === 'string' && d.filename ? d.filename : 'export.csv';
        const filename = raw.endsWith('.csv') ? raw : `${raw}.csv`;
        const blob = new Blob(['﻿', d.csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.warn('[Dashboard] No se pudo descargar el CSV delegado por el dashboard', err);
      }
    };
    window.addEventListener('message', handleLabDownload);
    return () => window.removeEventListener('message', handleLabDownload);
  }, [labDashboardOrigin]);

  /**
   * Externos (usr_type > 0) con licensed_until definido y ya vencido. El dashboard aplica
   * su propio paywall por licencia; aquí solo lo usamos para resaltar la fecha vencida en
   * el encabezado. Los internos conservan acceso completo.
   */
  const licenseExpired = useMemo(() => {
    if (!(Number(usr_type) > 0)) return false;
    if (licensed_until == null || String(licensed_until).trim() === '') return false;
    const m = String(licensed_until).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
    const lic = m
      ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
      : new Date(licensed_until);
    if (Number.isNaN(lic.getTime())) return false;
    lic.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return lic < today;
  }, [usr_type, licensed_until]);

  const filteredSerialList = useMemo(() => {
    if (!searchSerial.trim() && !searchSubCat.trim()) return SerialList;
    return SerialList.filter((s) => {
      const matchSerial = !searchSerial.trim() || (s.serial && String(s.serial).toLowerCase().includes(searchSerial.trim().toLowerCase()));
      const matchSubCat = !searchSubCat.trim() || (s.sub_cat && String(s.sub_cat).toLowerCase().includes(searchSubCat.trim().toLowerCase()));
      return matchSerial && matchSubCat;
    });
  }, [SerialList, searchSerial, searchSubCat]);

  useEffect(() => {
    const ids = filteredSerialList.map((s) => s.id);
    const nSel = ids.filter((sid) => selectedSerialIds.includes(sid)).length;
    const el = serialSelectAllRef.current;
    if (el) {
      el.indeterminate = nSel > 0 && nSel < ids.length;
    }
  }, [filteredSerialList, selectedSerialIds]);

  const pinMetaMaps = useMemo(() => {
    const mapById = (list) => {
      const m = new Map();
      (list || []).forEach((item) => {
        if (item && item.id !== undefined) {
          m.set(String(item.id), item.name || item.label || item.title || item.description || String(item.id));
        }
      });
      return m;
    };
    return {
      statusMap: mapById(pinsMeta.statusTypes),
      timeMap: mapById(pinsMeta.timeTypes),
      pinTypeMap: mapById(pinsMeta.pinTypes),
    };
  }, [pinsMeta]);

  const filteredVinculoPines = useMemo(() => {
    const q = searchPin.trim().toLowerCase();
    if (!q) return vinculoPines;
    return vinculoPines.filter((p) =>
      (p.pin && String(p.pin).toLowerCase().includes(q)) ||
      (p.email && String(p.email).toLowerCase().includes(q)) ||
      (p.userName && String(p.userName).toLowerCase().includes(q)) ||
      (p.serial_identify && String(p.serial_identify).toLowerCase().includes(q)) ||
      (p.userId != null && String(p.userId).includes(q)),
    );
  }, [vinculoPines, searchPin]);

  const sortedFilteredVinculoPines = useMemo(() => {
    const normalizeValue = (row, key) => {
      switch (key) {
        case 'pin': return String(row.pin || '').toLowerCase();
        case 'email': return String(row.email || '').toLowerCase();
        case 'userName': return String(row.userName || '').toLowerCase();
        case 'status': return String(pinMetaMaps.statusMap.get(String(row.status_type_id ?? '')) || row.status_type_id || '').toLowerCase();
        case 'serial': return String(row.serial_identify || '').toLowerCase();
        case 'timeType': return String(pinMetaMaps.timeMap.get(String(row.time_type_id ?? '')) || row.time_type_id || '').toLowerCase();
        case 'pinType': return String(pinMetaMaps.pinTypeMap.get(String(row.pin_type_id ?? '')) || row.pin_type_id || '').toLowerCase();
        case 'userId': return row.userId == null ? null : Number(row.userId);
        case 'activedAt': return row.actived_at ? new Date(row.actived_at).getTime() : null;
        case 'createdAt': return row.mpin_created_at ? new Date(row.mpin_created_at).getTime() : null;
        default: return '';
      }
    };
    const compare = (a, b) => {
      const av = normalizeValue(a, pinsSort.key);
      const bv = normalizeValue(b, pinsSort.key);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return av - bv;
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' });
    };
    const sorted = [...filteredVinculoPines].sort(compare);
    return pinsSort.direction === 'asc' ? sorted : sorted.reverse();
  }, [filteredVinculoPines, pinsSort, pinMetaMaps]);

  const togglePinsSort = useCallback((key) => {
    setPinsSort((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  }, []);

  const getPinsSortIndicator = useCallback((key) => {
    if (pinsSort.key !== key) return '↕';
    return pinsSort.direction === 'asc' ? '↑' : '↓';
  }, [pinsSort]);

  const vinculoPinesStats = useMemo(() => {
    const total = vinculoPines.length;
    const used = vinculoPines.filter((r) => r.userId != null && Number(r.userId) > 0).length;
    const free = Math.max(0, total - used);
    return { total, used, free };
  }, [vinculoPines]);

  const fetchProjectPins = useCallback(async (options = {}) => {
    const { replaceAll = false } = options;
    const projectId = parseInt(id, 10);
    if (!Number.isFinite(projectId) || projectId <= 0) return;
    setPinsLoading(true);
    try {
      const res = await get_project_pins(token, projectId);
      if (res == null) return;
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      const list = res?.pins;
      if (Array.isArray(list)) {
        const serverRows = list.map((row) => mapServerPinRowToState(row));
        if (replaceAll) {
          setVinculoPines(serverRows);
        } else {
          setVinculoPines((prev) => {
            const localRows = prev.filter((r) => r.dbId == null);
            return [...serverRows, ...localRows];
          });
        }
      } else if (replaceAll) {
        setVinculoPines([]);
      }
    } finally {
      setPinsLoading(false);
    }
  }, [id, token, navigate]);

  useEffect(() => {
    if (!isModalOpen || brevoModalTab !== 'pines') return;
    fetchProjectPins();
  }, [isModalOpen, brevoModalTab, fetchProjectPins]);

  useEffect(() => {
    const canLoadMeta =
      isModalOpen &&
      brevoModalTab === 'pines' &&
      (isProjectAdminInternalUser(usr_type) || Number(linkExternal) === 0);
    if (!canLoadMeta) return undefined;
    let cancelled = false;
    (async () => {
      const r = await pinMetaService.getMeta(token);
      if (cancelled || r === 'INVALID' || r == null) return;
      setPinsMeta({
        statusTypes: r.statusTypes || [],
        timeTypes: r.timeTypes || [],
        pinTypes: r.pinTypes || [],
      });
    })();
    return () => { cancelled = true; };
  }, [isModalOpen, brevoModalTab, token, usr_type, linkExternal]);

  useEffect(() => {
    if (!isModalOpen) {
      setSelectedPinKeys([]);
      setPendingCsvPins([]);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (pinsLoading) return;
    const keys = filteredVinculoPines.map(projectPinRowKey);
    const nSel = keys.filter((k) => selectedPinKeys.includes(k)).length;
    const el = pinSelectAllRef.current;
    if (el) {
      el.indeterminate = nSel > 0 && nSel < keys.length;
    }
  }, [filteredVinculoPines, selectedPinKeys, pinsLoading]);

  const refresh_projects_list = (id) => {
    async function fetchProjectSerials() {
        const membersList = await get_project_serials(token, id);
        if (membersList==="INVALID" || membersList == null){
          setEntryErrorMessage("El usuario no tiene permisos para ingresar a este dashboard.");
          setSerialList([]);
          setProject_opportunities([]);
          setnombre_proy('');
          setcountry('');
          setLicensedUntil(null);
          setViewerPermission(null);
          setlink_token('');
          setlink_id('');
          setlink_name('');
          setLinkExternal(0);
          setHasUnsavedChanges(false);
          setIsEditingLicensedUntil(false);
          return;
        }else{
          setEntryErrorMessage('');
          const projectInfo = getProjectRowFromSerialsResponse(membersList);
          const serials = Array.isArray(membersList?.serials) ? membersList.serials : [];

          const members = serials.map((element, index) => ({
            id: element.id,
            serial: element.tomi_serial,
            sub_cat: element.sub_cat,
            // Vínculo actual del TOMi8 a un usuario (serialization.usr_id_tomi).
            usr_id_tomi: element.usr_id_tomi ? Number(element.usr_id_tomi) : 0,
            linked_email: element.linked_email || '',
            linked_name: element.linked_name || '',
            last_usr_id_change: element.last_usr_id_change || null,
            ies_id: element.ies_id ? Number(element.ies_id) : 0,
            ies_name: element.ies_name || '',
            ies_country: element.ies_country || '',
            ies_city: element.ies_city || '',
            index,
          }));
          setSerialList(members);

          const loadedName = projectInfo?.project_name ?? '';
          const loadedCountry = projectInfo?.country ?? '';
          const loadedLicensedUntil = projectInfo?.licensed_until ?? null;
          const rawViewerPerm = projectInfo?.viewer_permission;
          const loadedViewerPerm =
            rawViewerPerm === null || rawViewerPerm === undefined || rawViewerPerm === ''
              ? null
              : Number(rawViewerPerm);
          setnombre_proy(loadedName);
          setcountry(loadedCountry);
          setLicensedUntil(loadedLicensedUntil);
          setViewerPermission(Number.isFinite(loadedViewerPerm) ? loadedViewerPerm : null);
          initialProjectValuesRef.current = { nombre_proy: loadedName, country: loadedCountry, licensed_until: loadedLicensedUntil };
          setHasUnsavedChanges(false);
          setlink_token (projectInfo?.link_token ?? '');
          setlink_id (projectInfo?.link_list_id ?? '');
          setlink_name (projectInfo?.link_project_name ?? '');
          const linkExternalNum = parseLinkExternalFromProjectRow(projectInfo);
          setLinkExternal(linkExternalNum);

          // Conteos: pines + BQ (id numérico) o Brevo + BQ (cliente siempre envía project_id; el servidor resuelve link_list_id)
          const pid = parseInt(id, 10);
          if (linkExternalNum === 0 && Number.isFinite(pid) && pid > 0) {
            loadPinsDashboardStatus(pid);
          } else if (Number.isFinite(pid) && pid > 0) {
            setPinsListTotalCount(null);
            setPinsRedeemedCount(null);
            loadBrevo_status(pid);
          } else {
            setBrevoElements('');
            setCloudElements('');
            setPinsListTotalCount(null);
            setPinsRedeemedCount(null);
          }

          const oppList = await get_project_opportunities(token, id);
          if (oppList !== "INVALID" && Array.isArray(oppList)) {
            setProject_opportunities(oppList);
          } else {
            setProject_opportunities([]);
          }
       }
      }
      fetchProjectSerials();
  }

  const countSerials =(e) =>{
    let seriales;
    seriales = e.split('\n');
    set_num_serials (seriales.length);

}


useEffect(() => {

  
  if (!(String(permission) === "1" || String(permission) === "2")) {
    navigate('/');
  }
}, [permission]); // Solo se vuelve a ejecutar si 'token' cambia


  
const btDelete_permission = (id_permission) => {
  if (!canManagePermissions) return;

    async function fetchDeletePermission(id_permission) {
      const membersList = await delete_projects_permission(token, id_permission);
      if (membersList==="INVALID"){
        navigate("/");
      }else{
          load_permissions(id);
     }
    }
    fetchDeletePermission(id_permission);
};

const btToggle_project_admin = (row) => {
  if (!canToggleProjectAdmin || !row?.id) return;

  const current = Number(row.permission) === 1 ? 1 : 0;
  const next = current === 1 ? 0 : 1;

  async function fetchToggleAdmin() {
    const updated = await update_projects_permission(token, row.id, next);
    if (updated === 'INVALID' || updated == null) {
      alert('No se pudo actualizar el permiso de administrador.');
      return;
    }
    if (Array.isArray(updated)) {
      const the_project_users = updated.map((element, index) => ({
        id: element?.id,
        email_id: element?.email_id,
        usr_name: element?.usr_name,
        permission: Number(element?.permission) === 1 ? 1 : 0,
        index,
      }));
      setpermissions_users(the_project_users);
    } else {
      load_permissions(id);
    }
  }
  fetchToggleAdmin();
};

const btAdd_permission = () => {
  if (!canManagePermissions) return;

  const email = String(newEmail ?? '').trim();
  if (!email) {
    alert('Ingresa un email para agregar el permiso.');
    return;
  }

  async function fetchDeletePermission() {
    const membersList = await insert_projects_permission(token, email, id);
    if (membersList==="INVALID"){
      navigate("/");
    } else if (membersList && typeof membersList === "object" && membersList.ok === false) {
      console.error("insert_projects_permission detalle:", membersList);
      const mysqlErr =
        (membersList.projects_permission && membersList.projects_permission.mysqli_error) ||
        (membersList.team && membersList.team.mysqli_error) ||
        "";
      const hint = membersList.hint ? "\n" + membersList.hint : "";
      alert(
        (membersList.message || "No se pudo insertar el permiso.") +
          (mysqlErr ? "\n\nMySQL: " + mysqlErr : "") +
          hint +
          "\n\nRevisa la consola (F12) para el objeto completo de depuración."
      );
    } else {
        load_permissions(id);
        // Limpiamos el formulario para que el usuario pueda agregar el siguiente contacto
        setnewMail('');
   }
  }
  fetchDeletePermission();
};

const btDelete = (id_relation) => {
  if (!canAddSerials) return;

  async function fetchDeleteRelation(id_relation) {
    const membersList = await delete_projects_relation(token, id_relation);
    if (membersList==="INVALID"){
      navigate("/");
    }else{
        refresh_projects_list(id);
        reloadDashboard();
   }
  }
  fetchDeleteRelation(id_relation);
};

const btUpdateProj = () => {
   

  async function fetchDeleteRelation() {
    const membersList = await update_project(token, id, nombre_proy, link_token, link_id, link_name, country, licensed_until ?? null);
    if (membersList==="INVALID"){
      navigate("/");
    }else{
        initialProjectValuesRef.current = { nombre_proy, country, licensed_until: licensed_until ?? null };
        setHasUnsavedChanges(false);
        setIsEditingLicensedUntil(false);
        refresh_projects_list(id);
   }
  }
  fetchDeleteRelation();
};

  
  useEffect(() => {
    if (id>0){
      refresh_projects_list(id);
    }
  }, [token, id]); // Solo se vuelve a ejecutar si 'token' cambia


  
  const insertSerialClick = () => {
    if (!canAddSerials) return;

    async function fetchChangeStatus() {
      
      if ( serialInputRef.current.value === ""){
        alert("Faltan datos");
        return;
      }

      seriales = serialInputRef.current.value.split('\n');
      set_ok_serials("");
      set_failed_serials("");

      

      let count = 0;
      for (const serial of seriales) {
        count +=1;
        set_procesados(count);
        if (serial !== "") {


            let serials_sub_cat = serial.split(",");

            const deviceStatus = await add_serial(token, serials_sub_cat[0], (serials_sub_cat[1] ?? ""), id);

            if (deviceStatus !== "OK") {
                set_failed_serials(prevString => prevString + "\n " + serials_sub_cat[0]);
            } else {
              // 9 Type Logistics annotation in issue_type
              if (deviceStatus === "INVALID") {
                set_failed_serials(prevString => prevString + "\n " + serials_sub_cat[0] );
              } else {
                set_ok_serials(prevString => prevString + "\n " + serials_sub_cat[0]);
              }
            }
        }
      }

      refresh_projects_list(id);
      reloadDashboard();
      }

      fetchChangeStatus();

  }


  
  const fill_linked_token = async (name, linkTokenVal, listId) => {
    setModalOpen(false);
    const listIdStr = String(listId ?? '');
    setlink_id(listIdStr);
    setlink_name(name);
    setlink_token(linkTokenVal);
    setLinkExternal(1);

    const projectId = parseInt(id, 10);
    if (!token || !Number.isFinite(projectId) || projectId <= 0) return;

    try {
      const extRes = await update_project_link_external(token, projectId, 1);
      if (extRes === 'INVALID') {
        navigate('/');
        return;
      }
      const updRes = await update_project(
        token,
        id,
        nombre_proy,
        linkTokenVal,
        listIdStr,
        name,
        country,
        licensed_until ?? null
      );
      if (updRes === 'INVALID') {
        navigate('/');
        return;
      }
      initialProjectValuesRef.current = { nombre_proy, country, licensed_until: licensed_until ?? null };
      setHasUnsavedChanges(false);
      refresh_projects_list(id);
      reloadDashboard();
    } catch (e) {
      console.error(e);
      alert('No se pudo guardar la lista de Brevo. Compruebe la conexión.');
    }
  };


/** usr_type === 0 = interno; usr_type > 0 = externo */
const usrTypeNum = parseInt(String(usr_type ?? '').trim(), 10);
const isInternalUser = usrTypeNum === 0;
const isExternalUser = Number.isFinite(usrTypeNum) && usrTypeNum > 0;
/** Externo con projects_permission.permission === 1 en este proyecto */
const isExternalProjectAdmin = isExternalUser && Number(viewerPermission) === 1;
const canManageProjectOps = isInternalUser || isExternalProjectAdmin;
/** Solo interno o externo admin: invitar/quitar miembros del proyecto */
const canManagePermissions = canManageProjectOps;
const canAddSerials = canManageProjectOps;
const canChangeBrevoList = canManageProjectOps;
const pinMgmtNum = Number(pin_management);
const canReassignPinEmail =
  (isInternalUser && (pinMgmtNum === 1 || pinMgmtNum === 3)) || isExternalProjectAdmin;

/** link_external: 1 = lista Brevo, 0 = pines internos */
const isPinesLinkMode = Number(linkExternal) === 0;

/** Externo no-admin con lista interna: ver pines sin editar ni cambiar vinculación */
const canViewPinesModalOnly = isExternalUser && !isExternalProjectAdmin && isPinesLinkMode;
/** Interno / externo admin siempre; externo miembro solo si link_external === 0 (pines) */
const canOpenBrevoPinsModal = canManageProjectOps || canViewPinesModalOnly;

const open_project_modal = () => {
  if (!canOpenBrevoPinsModal) return;
  if (canViewPinesModalOnly) {
    setBrevoModalTab('pines');
    setModalOpen(true);
    return;
  }
  if (isPinesLinkMode) {
    setBrevoModalTab('pines');
  } else {
    const hasToken = String(link_token ?? '').trim();
    setBrevoModalTab(hasToken ? 'brevo' : 'pines');
    // Ya en modo Brevo: cargar carpetas al abrir (antes solo se hacía al cambiar de pestaña).
    if (hasToken) {
      setFolderPage(true);
      loadBrevoFolders();
      const pidModal = parseInt(id, 10);
      if (Number.isFinite(pidModal) && pidModal > 0) loadBrevo_status(pidModal);
    }
  }
  setModalOpen(true);
};

const handleBrevoModalTabChange = (tab) => {
  if (!canChangeBrevoList) return;
  if (tab === brevoModalTab) return;

  // "Miembros" es SOLO una vista: no cambia el modo de vinculación del proyecto.
  // Las otras dos pestañas sí lo cambian (escriben projects.link_external), así que
  // al salir de Miembros se compara contra el MODO real y no contra la vista — si
  // no, volver a "Vincular pines" pediría confirmación para un cambio que no existe.
  if (tab === 'miembros') {
    setBrevoModalTab('miembros');
    return;
  }
  if (brevoModalTab === 'miembros' && tab === (isPinesLinkMode ? 'pines' : 'brevo')) {
    setBrevoModalTab(tab);
    return;
  }

  const goingPines = tab === 'pines';
  const msg = goingPines
    ? '¿Cambiar a vinculación por pines? Este será el metodo usado para actualizar la lista de usuarios en el dashboard'
    : '¿Cambiar a vinculación por lista de Brevo? Este será el metodo usado para actualizar la lista de usuarios en el dashboard.';
  if (!window.confirm(msg)) return;

  const projectId = parseInt(id, 10);
  if (!Number.isFinite(projectId) || projectId <= 0) return;

  const newLinkExternal = goingPines ? 0 : 1;
  (async () => {
    const res = await update_project_link_external(token, projectId, newLinkExternal);
    if (res == null) {
      alert('No se pudo actualizar el modo de vinculación. Compruebe la conexión.');
      return;
    }
    if (res === 'INVALID') {
      navigate('/');
      return;
    }
    setLinkExternal(newLinkExternal);
    setBrevoModalTab(tab);
    if (tab === 'brevo') {
      loadBrevoFolders();
      loadBrevo_status(projectId);
    } else {
      loadPinsDashboardStatus(projectId);
    }
  })();
};

const runFullPinesSyncAndDashboard = async ({ setHeaderProgress = false } = {}) => {
  if (!canOpenBrevoPinsModal) return;
  const projectId = parseInt(id, 10);
  if (!Number.isFinite(projectId) || projectId <= 0) return;

  if (setHeaderProgress) {
    setInProgress(true);
  }
  setBrevoElements(0);
  setCloudElements(0);
  setShowPinsSyncWaitModal(true);
  setPinsSyncRunning(true);
  setPinsSyncStep('batch');

  try {
    const res = await sync_project_pins_batch(token, projectId);
    if (res == null) {
      alert('No se pudo sincronizar el estado de los pines. Compruebe la conexión.');
      return;
    }
    if (res === 'INVALID') {
      navigate('/');
      return;
    }
    if (res.status === 'ERROR') {
      alert(res.message || 'Error al sincronizar los pines.');
      return;
    }
    if (res.status === 'OK' && Array.isArray(res.pins)) {
      setVinculoPines((prev) => {
        const localRows = prev.filter((r) => r.dbId == null);
        const serverRows = res.pins.map((row) => mapServerPinRowToState(row));
        return [...serverRows, ...localRows];
      });
    }

    setPinsSyncStep('bigquery');
    const bq = await sync_project_pins_to_bigquery(token, projectId);
    if (bq === 'INVALID') {
      navigate('/');
      return;
    }
    if (bq == null) {
      alert('No se pudo actualizar el dashboard (BigQuery). Compruebe la conexión.');
      return;
    }
    if (bq.status === 'ERROR') {
      alert(bq.message || 'Error al sincronizar el dashboard con BigQuery.');
      return;
    }
    // El sync a BigQuery reescribe el roster SIN ciudad/institución (borra los
    // filtros del dashboard) → re-aplicamos el enriquecimiento del master de IEs
    // ANTES de recargar el iframe, para que ya cargue con los filtros vivos.
    await refresh_ies_enrichment(token, projectId);
    loadPinsDashboardStatus(projectId);
    reloadDashboard();
  } finally {
    setPinsSyncStep(null);
    setPinsSyncRunning(false);
    setShowPinsSyncWaitModal(false);
    if (setHeaderProgress) {
      setInProgress(false);
    }
  }
};

const handleSyncProjectPinsStatus = async () => {
  if (!canOpenBrevoPinsModal) return;
  const projectId = parseInt(id, 10);
  if (!Number.isFinite(projectId) || projectId <= 0) return;

  if (isPinesLinkMode) {
    await runFullPinesSyncAndDashboard({ setHeaderProgress: false });
    return;
  }

  setShowPinsSyncWaitModal(true);
  setPinsSyncRunning(true);
  setPinsSyncStep('batch');
  try {
    const res = await sync_project_pins_batch(token, projectId);
    if (res == null) {
      alert('No se pudo sincronizar. Compruebe la conexión.');
      return;
    }
    if (res === 'INVALID') {
      navigate('/');
      return;
    }
    if (res.status === 'ERROR') {
      alert(res.message || 'Error al sincronizar los pines.');
      return;
    }
    if (res.status === 'OK' && Array.isArray(res.pins)) {
      setVinculoPines((prev) => {
        const localRows = prev.filter((r) => r.dbId == null);
        const serverRows = res.pins.map((row) => mapServerPinRowToState(row));
        return [...serverRows, ...localRows];
      });
      reloadDashboard();
    }
  } finally {
    setPinsSyncStep(null);
    setPinsSyncRunning(false);
    setShowPinsSyncWaitModal(false);
  }
};

const handleDownloadProjectPinsCsv = () => {
  if (pinsLoading) {
    alert('Espere a que termine la carga de pines.');
    return;
  }
  if (!sortedFilteredVinculoPines.length) {
    alert('No hay filas para exportar.');
    return;
  }
  const msg =
    'Para que el CSV refleje estado, email y demás datos al día, use primero «Actualizar estado». '
    + '¿Descargar el CSV con los datos actuales de la tabla?';
  if (!window.confirm(msg)) return;
  const csv = buildProjectPinsCsv(sortedFilteredVinculoPines, pinMetaMaps);
  downloadTextAsFile(formatProjectPinsCsvFilename(id), csv);
};

const handleImportPines = async () => {
  if (!canChangeBrevoList) return;
  const seen = new Set();
  const all = [];
  const pushUnique = (p) => {
    const t = String(p).trim();
    if (!t || seen.has(t)) return;
    seen.add(t);
    all.push(t);
  };

  if (pendingCsvPins.length > 0) {
    pendingCsvPins.forEach(pushUnique);
  } else {
    extractPinsFromCommaInput(pinInput).forEach(pushUnique);
    vinculoPines.forEach((r) => pushUnique(r.pin));
  }

  if (!all.length) {
    alert(
      pendingCsvPins.length
        ? 'No hay pines listos para importar desde el CSV.'
        : 'Agregue pines en el campo de texto o cargue un archivo CSV.',
    );
    return;
  }
  const projectId = parseInt(id, 10);
  if (!Number.isFinite(projectId) || projectId <= 0) return;

  setPinsSubmitting(true);
  try {
    const res = await insert_project_pins_batches(token, projectId, all);
    if (res == null) {
      alert('No se pudo guardar los pines. Compruebe la conexión.');
      return;
    }
    if (res === 'INVALID') {
      navigate('/');
      return;
    }
    if (res.status === 'INVALID') {
      alert(res.message || 'No se pudieron guardar los pines.');
      return;
    }
    setPinInput('');
    setPendingCsvPins([]);
    setSelectedPinKeys([]);
    await fetchProjectPins({ replaceAll: true });
    if (isPinesLinkMode) {
      loadPinsDashboardStatus(projectId);
    }
    reloadDashboard();
    alert(
      `Pines enviados: ${all.length}. Nuevos guardados: ${res.inserted}. Omitidos (duplicados u otros): ${res.skipped_duplicates}.`,
    );
  } finally {
    setPinsSubmitting(false);
  }
};

const handleCsvUpload = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (evt) => {
    const text = evt.target?.result ?? '';
    const parsed = extractPinsFromCsvOrList(text);
    if (!parsed.length) {
      alert('No se encontraron pines en el archivo.');
      return;
    }
    setPinInput('');
    setPendingCsvPins(parsed);
  };
  reader.readAsText(file);
  e.target.value = '';
};

const clearPendingCsvPins = () => {
  setPendingCsvPins([]);
};

const togglePinRowSelection = (key) => {
  setSelectedPinKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
};

const toggleSelectAllFilteredPins = () => {
  const keys = filteredVinculoPines.map(projectPinRowKey);
  const allOn = keys.length > 0 && keys.every((k) => selectedPinKeys.includes(k));
  if (allOn) {
    setSelectedPinKeys((prev) => prev.filter((k) => !keys.includes(k)));
  } else {
    setSelectedPinKeys((prev) => [...new Set([...prev, ...keys])]);
  }
};

const handleDeleteSelectedPins = async () => {
  if (!canChangeBrevoList) return;
  if (!selectedPinKeys.length) return;
  const n = selectedPinKeys.length;
  if (!window.confirm(`¿Eliminar ${n} pin${n === 1 ? '' : 'es'} seleccionado${n === 1 ? '' : 's'}? Esta acción no se puede deshacer.`)) {
    return;
  }
  const projectId = parseInt(id, 10);
  if (!Number.isFinite(projectId) || projectId <= 0) return;

  const dbIds = [];
  const localPins = new Set();
  selectedPinKeys.forEach((k) => {
    if (k.startsWith('id:')) {
      const num = parseInt(k.slice(3), 10);
      if (Number.isFinite(num) && num > 0) dbIds.push(num);
    } else if (k.startsWith('local:')) {
      localPins.add(k.slice(6));
    }
  });

  setPinsDeleting(true);
  try {
    if (dbIds.length) {
      const res = await delete_project_pins(token, projectId, dbIds);
      if (res == null) {
        alert('No se pudo eliminar. Compruebe la conexión.');
        return;
      }
      if (res === 'INVALID') {
        navigate('/');
        return;
      }
      if (res.status === 'INVALID') {
        alert(res.message || 'No se pudieron eliminar los pines.');
        return;
      }
    }
    if (localPins.size) {
      setVinculoPines((prev) => prev.filter((r) => r.dbId != null || !localPins.has(String(r.pin))));
    }
    setSelectedPinKeys([]);
    if (dbIds.length) {
      await fetchProjectPins();
      if (isPinesLinkMode) {
        loadPinsDashboardStatus(projectId);
      }
      reloadDashboard();
    }
  } finally {
    setPinsDeleting(false);
  }
};

const handleDeleteOnePinRow = async (row) => {
  if (!canChangeBrevoList) return;
  const key = projectPinRowKey(row);
  if (!window.confirm(`¿Eliminar el pin "${row.pin}"? Esta acción no se puede deshacer.`)) {
    return;
  }
  const projectId = parseInt(id, 10);
  if (!Number.isFinite(projectId) || projectId <= 0) return;

  if (row.dbId == null) {
    setVinculoPines((prev) => prev.filter((r) => !(r.dbId == null && r.pin === row.pin)));
    setSelectedPinKeys((prev) => prev.filter((k) => k !== key));
    return;
  }

  setPinsDeleting(true);
  try {
    const res = await delete_project_pins(token, projectId, [row.dbId]);
    if (res == null) {
      alert('No se pudo eliminar. Compruebe la conexión.');
      return;
    }
    if (res === 'INVALID') {
      navigate('/');
      return;
    }
    if (res.status === 'INVALID') {
      alert(res.message || 'No se pudo eliminar el pin.');
      return;
    }
    setSelectedPinKeys((prev) => prev.filter((k) => k !== key));
    await fetchProjectPins();
    if (isPinesLinkMode) {
      loadPinsDashboardStatus(projectId);
    }
    reloadDashboard();
  } finally {
    setPinsDeleting(false);
  }
};

const closePinAssignModal = () => {
  if (pinAssignSubmitting) return;
  setPinAssignModal(null);
  setPinAssignNewEmail('');
  setPinAssignConfirmEmail('');
};

const handlePinEmailEditClick = (row) => {
  if (!row?.email) return;
  if (!canReassignPinEmail) {
    alert('Para reasignar un PIN a otro usuario, comuniquese con el administrador');
    return;
  }
  setPinAssignModal({ pin: row.pin, currentEmail: row.email });
  setPinAssignNewEmail('');
  setPinAssignConfirmEmail('');
};

const handleConfirmPinAssign = async () => {
  if (!pinAssignModal || !canReassignPinEmail) return;
  const newEmail = String(pinAssignNewEmail || '').trim().toLowerCase();
  const confirmEmail = String(pinAssignConfirmEmail || '').trim().toLowerCase();
  if (!newEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
    alert('Ingrese un email de destino válido.');
    return;
  }
  if (newEmail !== confirmEmail) {
    alert('El email de confirmación no coincide con el email de destino.');
    return;
  }
  if (newEmail === String(pinAssignModal.currentEmail || '').trim().toLowerCase()) {
    alert('El email de destino es igual al actual.');
    return;
  }
  const projectId = parseInt(id, 10);
  if (!Number.isFinite(projectId) || projectId <= 0) return;

  setPinAssignSubmitting(true);
  try {
    const res = await pinAssignService.assign({
      token,
      pin: pinAssignModal.pin,
      email: newEmail,
      project_id: projectId,
    });
    if (res == null) {
      alert('No se pudo reasignar el PIN. Compruebe la conexión.');
      return;
    }
    if (res === 'INVALID') {
      navigate('/');
      return;
    }
    if (res.error) {
      const msg = res.message || res.error;
      const status = res.debug?.http_status;
      const upstream = res.debug?.response_body;
      let detail = msg;
      if (status != null) detail += ` (HTTP ${status})`;
      if (upstream != null) {
        detail += '\n\nRespuesta API: ' + (typeof upstream === 'string' ? upstream : JSON.stringify(upstream));
      }
      if (String(msg).includes('validation.server.user.not_found')
        || (typeof upstream === 'object' && String(upstream?.validation || '').includes('not_found'))) {
        alert('No se encontró un usuario con ese email.');
      } else if (String(msg).includes('validation.server.user.mismatch')
        || (typeof upstream === 'object' && String(upstream?.validation || '').includes('mismatch'))) {
        alert('El usuario no coincide. Verifique el email.');
      } else {
        alert(detail || 'No se pudo reasignar el PIN.');
      }
      return;
    }
    setPinAssignModal(null);
    setPinAssignNewEmail('');
    setPinAssignConfirmEmail('');
    await fetchProjectPins();
    if (isPinesLinkMode) {
      loadPinsDashboardStatus(projectId);
    }
    reloadDashboard();
  } finally {
    setPinAssignSubmitting(false);
  }
};

const open_modificar_permisos = (token) => {
  setModalPermisos(true);
  load_permissions(id);
};

const update_bigq_list = () => {
  setBrevoElements(0);
  setCloudElements(0);
  const projectId = parseInt(id, 10);

  if (isPinesLinkMode) {
    if (!Number.isFinite(projectId) || projectId <= 0) return;
    if (pinsSyncRunning) return;
    runFullPinesSyncAndDashboard({ setHeaderProgress: true });
    return;
  }

  setInProgress(true);
  async function fetchBrevoLists() {
    const csv_request_initial = await request_brevo_csv(token, link_id, link_token);
    if (csv_request_initial === "error") {
      navigate("/"); // Redirige si hay error
      return;
    }

    let timeout_counter = 0; // Contador de intentos
    const intervalo = setInterval(async () => {
      try {
        const process_status = await validate_procces(csv_request_initial);
        if (process_status === "finished") {
          setInProgress(false);
          clearInterval(intervalo); // Detenemos el intervalo si está listo
          loadBrevo_status(projectId);
        } else if (timeout_counter >= 10) {
          clearInterval(intervalo); // Detenemos el intervalo si supera el límite
          setInProgress(false);
        }
        timeout_counter++; // Incrementa el contador después de cada ciclo
      } catch (error) {
        console.error("Error en la validación del proceso:", error);
        clearInterval(intervalo);
        setInProgress(false);
      }
    }, 3000);
  }

  fetchBrevoLists(); // Inicia el proceso
};


const load_permissions = (id) => {

  async function fetchBrevoFolders() {
    const permissionList = await get_project_permissions(token, id);
      if (permissionList==="INVALID"){
        navigate("/");
      }else{
        // Backend puede devolver `null` cuando no hay permisos.
        // Evitamos romper el modal haciendo "map" sobre `[]`.
        const permissionListSafe = Array.isArray(permissionList) ? permissionList : [];
        const the_project_users = permissionListSafe.map((element, index) => {
          return {
            id: element?.id,
            email_id: element?.email_id,
            usr_name: element?.usr_name,
            permission: Number(element?.permission) === 1 ? 1 : 0,
            index: index,
          };
        });
        setpermissions_users(the_project_users);
                
   }
  }


    fetchBrevoFolders();

};




const loadBrevoFolders = () => {

  async function fetchBrevoFolders() {
    const foldersList = await get_brevo_folders(token);
      if (foldersList==="INVALID"){
        navigate("/");
      }else{
        // Backend puede devolver `null` si no hay carpetas.
        const foldersListSafe = Array.isArray(foldersList) ? foldersList : [];
        const the_folders_brevo = foldersListSafe.map((element, index) => {
          return { id: element?.id, name: element?.name, index: index };
        });
        setfolders_brevo(the_folders_brevo);
                
   }
  }


    fetchBrevoFolders();

};

const loadFolderLists = (listid) =>{

  async function fetchBrevoLists() {
    const projectsList = await get_brevo_lists(token, listid);
    if (projectsList==="INVALID"){
      navigate("/");
    }else{
      const the_projects_brevo = projectsList.map((element, index) => {
        let splitedName = element.name.split("-");

        return {id:element.id, name: element.name, link_token: md5(splitedName[0]), users: element.uniqueSubscribers , index: index };
      }).sort((a, b) => a.name.localeCompare(b.name));;
      setprojects_brevo(the_projects_brevo);
              
   }
  }

  fetchBrevoLists();
};


const loadBrevo_status = (projectId) =>{

  async function fetchBrevoStatus(pid) {
    setPinsListTotalCount(null);
    setPinsRedeemedCount(null);
    const list_update_status = await get_project_updt_status(token, pid);
          if (list_update_status==="INVALID"){

          }else{
            setBrevoElements(list_update_status['uniqueInBrevo']);
            setCloudElements(list_update_status['bigQelements']);            
          }
  }

  fetchBrevoStatus(projectId);
};

const loadPinsDashboardStatus = (projectId) => {
  async function run(pid) {
    const res = await get_project_pins_dashboard_status(token, pid);
    if (res === 'INVALID' || res == null) return;
    if (res.error === 'NotPinsMode') return;
    setBrevoElements(res.pinsWithUser ?? res.uniqueInBrevo ?? 0);
    setCloudElements(res.bigQelements ?? 0);
    setPinsRedeemedCount(res.pinsWithUser != null ? Number(res.pinsWithUser) : null);
    setPinsListTotalCount(
      res.pinsTotal != null && res.pinsTotal !== ''
        ? Number(res.pinsTotal)
        : null
    );
  }
  run(projectId);
};


const load_folderlists= (listid, foldername) => {
  setcurrentfolderName(foldername);
  loadFolderLists(listid);
  setFolderPage(false);
  
};

const handleGoBack= (listid) => {
  setFolderPage(true);
  
};





  const openListaSerialesModal = () => {
    setSerialListView('list');
    setModalListaSerialesOpen(true);
  };

  // Actualiza en estado la fila de un serial tras vincularlo a un usuario, sin recargar todo.
  const applySerialLink = (serialId, linked) => {
    setSerialList((prev) =>
      prev.map((s) =>
        s.id === serialId
          ? {
              ...s,
              usr_id_tomi: linked.user_id,
              linked_name: linked.user_name || '',
              linked_email: linked.user_email || '',
              last_usr_id_change: linked.last_usr_id_change || s.last_usr_id_change,
            }
          : s
      )
    );
  };

  // Actualiza en estado el sub_cat de un serial tras editarlo, sin recargar todo.
  const applySerialSubCat = (serialId, subCat) => {
    setSerialList((prev) => prev.map((s) => (s.id === serialId ? { ...s, sub_cat: subCat } : s)));
  };

  const applySerialIes = (serialNumbers, ies) => {
    const setNums = new Set((serialNumbers || []).map((s) => String(s)));
    setSerialList((prev) =>
      prev.map((s) => {
        if (!setNums.has(String(s.serial))) return s;
        if (!ies) {
          return { ...s, ies_id: 0, ies_name: '', ies_country: '', ies_city: '' };
        }
        return {
          ...s,
          ies_id: Number(ies.id) || 0,
          ies_name: ies.name || '',
          ies_country: ies.country || '',
          ies_city: ies.city || '',
        };
      })
    );
  };

  const applyPinIes = (pinCodes, ies) => {
    const setPins = new Set((pinCodes || []).map((p) => String(p)));
    setVinculoPines((prev) =>
      prev.map((row) => {
        if (!setPins.has(String(row.pin))) return row;
        if (!ies) {
          return { ...row, ies_id: 0, ies_name: '', ies_country: '', ies_city: '' };
        }
        return {
          ...row,
          ies_id: Number(ies.id) || 0,
          ies_name: ies.name || '',
          ies_country: ies.country || '',
          ies_city: ies.city || '',
        };
      })
    );
  };

  const openIesPickerForSerial = (serialRow) => {
    if (!serialRow?.serial) return;
    setIesPickerKind('serial');
    setIesPickerItems([serialRow.serial]);
    setIesPickerOpen(true);
  };

  const openIesPickerForSelected = () => {
    const serials = filteredSerialList
      .filter((s) => selectedSerialIds.includes(s.id))
      .map((s) => s.serial)
      .filter(Boolean);
    if (serials.length === 0) return;
    setIesPickerKind('serial');
    setIesPickerItems(serials);
    setIesPickerOpen(true);
  };

  const openIesPickerForPin = (pinRow) => {
    if (!pinRow?.pin || pinRow.dbId == null) return;
    setIesPickerKind('pin');
    setIesPickerItems([pinRow.pin]);
    setIesPickerOpen(true);
  };

  const openIesPickerForSelectedPins = () => {
    const pins = sortedFilteredVinculoPines
      .filter((r) => selectedPinKeys.includes(projectPinRowKey(r)) && r.dbId != null && r.pin)
      .map((r) => r.pin);
    if (pins.length === 0) return;
    setIesPickerKind('pin');
    setIesPickerItems(pins);
    setIesPickerOpen(true);
  };

  const closeIesPicker = () => {
    setIesPickerOpen(false);
    setIesPickerItems([]);
  };

  const handleIesPickerSelect = async (ie) => {
    if (!iesPickerItems.length) return;
    const iesId = ie ? Number(ie.id) : 0;
    if (iesPickerKind === 'pin') {
      const result = await set_pin_ies(token, id, iesPickerItems, iesId);
      if (result === 'INVALID') {
        navigate('/');
        return;
      }
      if (!result?.ok) {
        window.alert(result?.error || 'No se pudo actualizar la institución.');
        return;
      }
      applyPinIes(iesPickerItems, ie);
      setSelectedPinKeys([]);
      closeIesPicker();
      reloadDashboard();
      return;
    }

    const result = await set_serial_ies(token, id, iesPickerItems, iesId);
    if (result === 'INVALID') {
      navigate('/');
      return;
    }
    if (!result?.ok) {
      window.alert(result?.error || 'No se pudo actualizar la institución.');
      return;
    }
    applySerialIes(iesPickerItems, ie);
    setSelectedSerialIds([]);
    closeIesPicker();
    reloadDashboard();
  };

  const toggleSerialSelected = (serialId) => {
    setSelectedSerialIds((prev) =>
      prev.includes(serialId) ? prev.filter((x) => x !== serialId) : [...prev, serialId]
    );
  };

  const toggleSelectAllFilteredSerials = () => {
    const ids = filteredSerialList.map((s) => s.id);
    const allSelected = ids.length > 0 && ids.every((sid) => selectedSerialIds.includes(sid));
    if (allSelected) {
      setSelectedSerialIds((prev) => prev.filter((sid) => !ids.includes(sid)));
    } else {
      setSelectedSerialIds((prev) => Array.from(new Set([...prev, ...ids])));
    }
  };

  const closeListaSerialesModal = () => {
    setModalListaSerialesOpen(false);
    setSerialListView('list');
    setSelectedSerialIds([]);
    closeIesPicker();
  };

  return (
    <div className="containerChk project-page">
      {entryErrorMessage ? (
        <div className="project-entry-error" role="alert">
          {entryErrorMessage}
        </div>
      ) : null}
        {/* Header: izquierda (datos), centro (conteo + TOMi8), derecha (Brevo + botones) */}
        <div className="project-header-card">
          <div className="project-header-left">
            <div className="project-header-title-block">
              <div className="project-header-title project-header-title-row">
                <input
                  type="text"
                  id="nombre"
                  className="project-header-title-input"
                  value={nombre_proy}
                  onChange={(e) => {
                    const next = e.target.value;
                    setnombre_proy(next);
                    const base = initialProjectValuesRef.current;
                    const baseLicensed = base.licensed_until ?? null;
                    setHasUnsavedChanges(
                      next !== (base.nombre_proy ?? '') || country !== (base.country ?? '')
                      || (licensed_until ?? null) !== baseLicensed
                    );
                  }}
                  placeholder="Nombre del proyecto"
                />
                <div className="project-header-country-select">
                  <Select
                    classNamePrefix="react-select"
                    options={countryOptions}
                    isLoading={countryOptions.length === 0}
                    formatOptionLabel={formatCountryOptionLabel}
                    onChange={(selectedOption) => {
                      const next = selectedOption?.value ?? '';
                      setcountry(next);
                      const base = initialProjectValuesRef.current;
                      const baseLicensed = base.licensed_until ?? null;
                      setHasUnsavedChanges(
                        nombre_proy !== (base.nombre_proy ?? '') || next !== (base.country ?? '')
                        || (licensed_until ?? null) !== baseLicensed
                      );
                    }}
                    value={getCountryOptionByValue(country)}
                    placeholder="País..."
                    isSearchable
                    styles={{
                      container: (provided) => ({ ...provided, width: '100%' }),
                      control: (provided) => ({
                        ...provided,
                        minHeight: 34,
                        height: 34,
                        borderRadius: 6,
                      }),
                      valueContainer: (provided) => ({ ...provided, height: 34, padding: '0 8px' }),
                      indicatorsContainer: (provided) => ({ ...provided, height: 34 }),
                      singleValue: (provided) => ({ ...provided, margin: 0, color: '#111827' }),
                      input: (provided) => ({ ...provided, color: '#111827' }),
                      placeholder: (provided) => ({ ...provided, color: '#6b7280' }),
                      option: (provided, state) => ({
                        ...provided,
                        color: '#111827',
                        backgroundColor: state.isSelected
                          ? '#e5e7eb'
                          : state.isFocused
                            ? '#f3f4f6'
                            : '#ffffff',
                      }),
                      menu: (provided) => ({ ...provided, zIndex: 9999 }),
                    }}
                  />
                </div>
                <button
                  type="button"
                  className="project-header-btn project-header-btn-icon project-header-share-btn"
                  onClick={() => open_modificar_permisos()}
                  title="Permisos / compartir"
                  aria-label="Administrar permisos de usuarios"
                >
                  <i className="bi bi-people" aria-hidden="true" />
                </button>
              </div>
              <div className="project-header-meta project-header-meta-compact">
                {canEditLicensedUntil ? (
                  <span className="project-header-meta-item">
                    <span className="project-header-label-sm">Venta:</span>
                    {project_opportunities.length === 0 ? (
                      <span className="project-header-muted-sm">Ninguna</span>
                    ) : (
                      <ul className="project-opportunities-list project-header-opp-list project-header-opp-inline">
                        {project_opportunities.map((opp) => (
                          <li key={opp.id}>
                            <a
                              href={`/opportunitypage?id=${opp.id}`}
                              onClick={(e) => {
                                e.preventDefault();
                                navigate(`/opportunitypage?id=${opp.id}`);
                              }}
                            >
                              {opp.name || `Oportunidad #${opp.id}`}
                            </a>
                          </li>
                        ))}
                      </ul>
                    )}
                  </span>
                ) : null}
                <div className={`project-header-license ${licenseExpired ? 'is-expired' : ''}`}>
                  <span className="project-header-label-sm">Licencia:</span>
                  {isEditingLicensedUntil ? (
                    <input
                      className="project-license-date-input project-license-date-input-compact"
                      type="date"
                      value={licensed_until ?? ''}
                      onChange={(e) => {
                        const nextValue = e.target.value ?? '';
                        const nextNormalized = nextValue === '' ? null : nextValue;
                        setLicensedUntil(nextNormalized);
                        const base = initialProjectValuesRef.current;
                        setHasUnsavedChanges(
                          nombre_proy !== (base.nombre_proy ?? '') ||
                          country !== (base.country ?? '') ||
                          (nextNormalized ?? null) !== (base.licensed_until ?? null)
                        );
                      }}
                      aria-label="Editar licencia de uso hasta"
                      title="Luego de esta fecha el dashboard solo será visible para usuarios internos."
                    />
                  ) : canEditLicensedUntil ? (
                    <button
                      type="button"
                      className="project-license-text-btn"
                      onClick={() => setIsEditingLicensedUntil(true)}
                      title="Luego de esta fecha el dashboard solo será visible para usuarios internos."
                      aria-label={licensed_until ? 'Editar fecha de licencia' : 'Establecer licencia'}
                    >
                      {licensed_until ?? 'Establecer licencia'}
                    </button>
                  ) : (
                    <span
                      className="project-header-muted-sm"
                      title={
                        licensed_until
                          ? 'Luego de esta fecha el dashboard solo será visible para usuarios internos.'
                          : undefined
                      }
                      aria-label="Licencia de uso externo: fecha de vencimiento"
                    >
                      {licensed_until ?? '—'}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Columna 3: lista de Brevo vinculada o modo pines */}
          <div className={`project-header-brevo${isPinesLinkMode ? ' is-pines-mode' : ''}`}>
            <div className="project-header-list-brevo">
              <div className={`list-brevo-main project-header-brevo-row${isPinesLinkMode ? ' is-pines-mode' : ''}`}>
                {canOpenBrevoPinsModal ? (
                  <button
                    type="button"
                    className="project-header-brevo-select"
                    onClick={() => open_project_modal()}
                    title={
                      canViewPinesModalOnly
                        ? 'Ver pines del proyecto (solo lectura)'
                        : isPinesLinkMode
                          ? 'Gestionar pines del proyecto'
                          : 'Cambiar lista vinculada'
                    }
                    aria-label={
                      canViewPinesModalOnly
                        ? 'Ver pines del proyecto, solo lectura'
                        : isPinesLinkMode
                          ? 'Gestionar pines del proyecto'
                          : 'Cambiar lista vinculada'
                    }
                  >
                    {isPinesLinkMode
                      ? `Lista Pines (${pinsRedeemedCount != null ? pinsRedeemedCount : '—'}/${pinsListTotalCount != null ? pinsListTotalCount : '—'} usados)`
                      : (
                        link_name?.trim()
                          ? `${link_name} (${CloudElements !== '' && CloudElements != null ? CloudElements : '—'}/${BrevoElements !== '' && BrevoElements != null ? BrevoElements : '—'})`
                          : 'Seleccionar lista de brevo'
                      )}
                  </button>
                ) : (
                  <div className="project-header-brevo-select project-header-brevo-select-readonly" aria-label="Lista vinculada (solo lectura)">
                    {isPinesLinkMode
                      ? `Lista Pines (${pinsRedeemedCount != null ? pinsRedeemedCount : '—'}/${pinsListTotalCount != null ? pinsListTotalCount : '—'} usados)`
                      : (
                        link_name?.trim()
                          ? `${link_name} (${CloudElements !== '' && CloudElements != null ? CloudElements : '—'}/${BrevoElements !== '' && BrevoElements != null ? BrevoElements : '—'})`
                          : 'Sin lista vinculada'
                      )}
                  </div>
                )}
                {!isPinesLinkMode ? (
                  <button
                    type="button"
                    className={`list-brevo-icon-btn project-header-icon-btn ${inProgress || pinsSyncRunning ? 'btn-update-progress' : ''}`}
                    onClick={() => update_bigq_list()}
                    disabled={(inProgress || pinsSyncRunning) || !link_id}
                    title={
                      inProgress || pinsSyncRunning
                        ? 'Actualizando…'
                        : 'Actualizar lista desde Brevo hacia el dashboard'
                    }
                  >
                    ⟳
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {/* Columna 4: botones de acciones */}
          <div className="project-header-actions">
            {hasUnsavedChanges ? (
              <button type="button" className="project-header-btn" onClick={btUpdateProj}>
                Guardar Cambios
              </button>
            ) : null}
          </div>

          {/* Columna final derecha: conteo de seriales (abre modal) */}
          <button
            type="button"
            className="project-header-center project-header-center-right"
            onClick={openListaSerialesModal}
            aria-label="Ver lista de seriales"
            title="Ver lista de seriales"
          >
            <span className="project-header-count">{SerialList.length}</span>
            <span className="project-header-tomi8">TOMi8</span>
          </button>
          {canOpenBrevoPinsModal ? (
            <>
            <Modal_proyectos
              isOpen={isModalOpen}
              onClose={() => setModalOpen(false)}
              title=""
            >
              {canChangeBrevoList ? (
                <div className="brevo-modal-tabs">
                  <button
                    type="button"
                    className={`brevo-modal-tab ${brevoModalTab === 'pines' ? 'active' : ''}`}
                    onClick={() => handleBrevoModalTabChange('pines')}
                  >
                    Vincular pines
                  </button>
                  <button
                    type="button"
                    className={`brevo-modal-tab ${brevoModalTab === 'brevo' ? 'active' : ''}`}
                    onClick={() => handleBrevoModalTabChange('brevo')}
                  >
                    Vincular lista de brevo
                  </button>
                  {isInternalUser ? (
                    <button
                      type="button"
                      className={`brevo-modal-tab brevo-modal-tab-right ${brevoModalTab === 'miembros' ? 'active' : ''}`}
                      onClick={() => handleBrevoModalTabChange('miembros')}
                      title="Todos los maestros del proyecto y por qué se están contando"
                    >
                      Maestros
                    </button>
                  ) : null}
                </div>
              ) : null}

              {brevoModalTab === 'miembros' ? (
                <ProjectMembers
                  token={token}
                  projectId={parseInt(id, 10)}
                  canEdit={canManageProjectOps}
                  onDataChanged={reloadDashboard}
                />
              ) : (canChangeBrevoList && brevoModalTab === 'brevo') ? (
                <div className="brevo-link-modal-content">
                  {!folderPage ? (
                    <button
                      type="button"
                      className="brevo-link-back"
                      onClick={() => handleGoBack()}
                      title="Volver a carpetas"
                    >
                      <i className="bi bi-arrow-left" aria-hidden="true" /> {currentfolderName}
                    </button>
                  ) : null}

                  <div className="brevo-link-table-card">
                    {!folderPage ? (
                      <div className="table-container3">
                        <table className="version-table3 brevo-link-table">
                          <thead>
                            <tr>
                              <th style={{ width: '45%' }}>Nombre</th>
                              <th style={{ width: '45%' }}>link_token</th>
                              <th style={{ width: '10%' }}># Users</th>
                            </tr>
                          </thead>
                          <tbody>
                            {projects_brevo.map((project, index) => (
                              <tr key={index} onClick={() => fill_linked_token(project.name, project.link_token, project.id)}>
                                <td>{project.name}</td>
                                <td className="brevo-link-token">{project.link_token}</td>
                                <td>{project.users}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="table-container3">
                        <table className="version-table3 brevo-link-table">
                          <thead>
                            <tr>
                              <th style={{ width: '100%' }}>Carpeta</th>
                            </tr>
                          </thead>
                          <tbody>
                            {folders_brevo.map((folder, index) => (
                              <tr key={index} onClick={() => load_folderlists(folder.id, folder.name)}>
                                <td>{folder.name}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="brevo-link-modal-content">
                  {canViewPinesModalOnly ? (
                    <p className="brevo-pines-readonly-banner">
                      Solo consulta: no puede agregar ni eliminar pines. Puede actualizar el estado de los pines y el dashboard.
                    </p>
                  ) : null}
                  <div className="brevo-pines-toolbar">
                    <input
                      type="text"
                      className="brevo-pines-search"
                      placeholder="Buscar pin..."
                      value={searchPin}
                      onChange={(e) => setSearchPin(e.target.value)}
                    />
                    <button
                      type="button"
                      className="brevo-pines-btn"
                      onClick={handleSyncProjectPinsStatus}
                      disabled={
                        pinsLoading ||
                        pinsSyncRunning ||
                        pinsSubmitting ||
                        pinsDeleting ||
                        !vinculoPines.some((r) => r.dbId != null)
                      }
                      title={
                        isPinesLinkMode
                          ? 'Actualizar estado en mpins y sincronizar el dashboard (BigQuery). Puede tardar varios minutos.'
                          : 'Consultar estado en el sistema de PINs (puede tardar varios minutos)'
                      }
                    >
                      {pinsSyncRunning ? 'Actualizando…' : 'Actualizar estado'}
                    </button>
                    <button
                      type="button"
                      className="brevo-pines-btn brevo-pines-btn-csv"
                      onClick={handleDownloadProjectPinsCsv}
                      disabled={
                        pinsLoading ||
                        sortedFilteredVinculoPines.length === 0 ||
                        pinsSyncRunning
                      }
                      title="Descargar la tabla visible en CSV. Actualice el estado antes para datos al día."
                      aria-label="Descargar tabla de pines en CSV"
                    >
                      <i className="bi bi-download" aria-hidden="true" />
                      CSV
                    </button>
                    {canChangeBrevoList && selectedPinKeys.length > 0 ? (
                      <button
                        type="button"
                        className="brevo-pines-btn brevo-pines-btn-danger"
                        onClick={handleDeleteSelectedPins}
                        disabled={pinsDeleting || pinsSubmitting || pinsLoading}
                      >
                        {pinsDeleting ? 'Eliminando…' : `Eliminar (${selectedPinKeys.length})`}
                      </button>
                    ) : null}
                    {isPinesLinkMode && canChangeBrevoList && selectedPinKeys.length > 0 ? (
                      <button
                        type="button"
                        className="brevo-pines-btn"
                        onClick={openIesPickerForSelectedPins}
                        disabled={pinsDeleting || pinsSubmitting || pinsLoading}
                      >
                        Cambiar IE ({selectedPinKeys.length})
                      </button>
                    ) : null}
                  </div>

                  <div className="brevo-link-table-card">
                    <div className="table-container3">
                      <table className="version-table3 brevo-link-table brevo-pines-table">
                        <thead>
                          <tr>
                            {canChangeBrevoList ? (
                              <th className="brevo-pines-th-check" aria-label="Seleccionar visibles">
                                <input
                                  ref={pinSelectAllRef}
                                  type="checkbox"
                                  checked={
                                    !pinsLoading &&
                                    sortedFilteredVinculoPines.length > 0 &&
                                    sortedFilteredVinculoPines.every((r) => selectedPinKeys.includes(projectPinRowKey(r)))
                                  }
                                  onChange={toggleSelectAllFilteredPins}
                                  disabled={pinsLoading || sortedFilteredVinculoPines.length === 0}
                                />
                              </th>
                            ) : null}
                            <th className="brevo-pines-sortable" onClick={() => togglePinsSort('pin')}>PIN <span>{getPinsSortIndicator('pin')}</span></th>
                            {isPinesLinkMode ? (
                              <th>Institución</th>
                            ) : null}
                            <th className="brevo-pines-sortable" onClick={() => togglePinsSort('email')}>Email <span>{getPinsSortIndicator('email')}</span></th>
                            <th className="brevo-pines-sortable" onClick={() => togglePinsSort('userName')}>Nombre <span>{getPinsSortIndicator('userName')}</span></th>
                            <th className="brevo-pines-sortable" onClick={() => togglePinsSort('status')}>Estado <span>{getPinsSortIndicator('status')}</span></th>
                            <th className="brevo-pines-sortable" onClick={() => togglePinsSort('serial')}>Serial <span>{getPinsSortIndicator('serial')}</span></th>
                            <th className="brevo-pines-sortable" onClick={() => togglePinsSort('timeType')}>Vigencia <span>{getPinsSortIndicator('timeType')}</span></th>
                            <th className="brevo-pines-sortable" onClick={() => togglePinsSort('pinType')}>Tipo PIN <span>{getPinsSortIndicator('pinType')}</span></th>
                            <th className="brevo-pines-sortable" onClick={() => togglePinsSort('userId')}>User ID <span>{getPinsSortIndicator('userId')}</span></th>
                            <th className="brevo-pines-sortable" onClick={() => togglePinsSort('activedAt')}>Activación <span>{getPinsSortIndicator('activedAt')}</span></th>
                            <th className="brevo-pines-sortable" onClick={() => togglePinsSort('createdAt')}>Creado (mpins) <span>{getPinsSortIndicator('createdAt')}</span></th>
                            {canChangeBrevoList ? (
                              <th className="brevo-pines-th-actions" aria-label="Eliminar" />
                            ) : null}
                          </tr>
                        </thead>
                        <tbody>
                          {pinsLoading ? (
                            <tr>
                              <td colSpan={canChangeBrevoList ? (isPinesLinkMode ? 13 : 12) : (isPinesLinkMode ? 11 : 10)} className="permission-empty">
                                Cargando pines…
                              </td>
                            </tr>
                          ) : (
                            <>
                              {sortedFilteredVinculoPines.map((row, idx) => {
                                const rowKey = projectPinRowKey(row);
                                return (
                                  <tr key={row.dbId != null ? String(row.dbId) : `local-${row.pin}-${idx}`}>
                                    {canChangeBrevoList ? (
                                      <td className="brevo-pines-td-check">
                                        <input
                                          type="checkbox"
                                          checked={selectedPinKeys.includes(rowKey)}
                                          onChange={() => togglePinRowSelection(rowKey)}
                                          aria-label={`Seleccionar pin ${row.pin}`}
                                        />
                                      </td>
                                    ) : null}
                                    <td>{row.pin}</td>
                                    {isPinesLinkMode ? (
                                      <td className="serials-cell-link">
                                        <SerialIesCell
                                          serial={{
                                            ies_id: row.ies_id,
                                            ies_name: row.ies_name,
                                            ies_country: row.ies_country,
                                            ies_city: row.ies_city,
                                          }}
                                          canEdit={canChangeBrevoList && row.dbId != null}
                                          onRequestPick={() => openIesPickerForPin(row)}
                                        />
                                      </td>
                                    ) : null}
                                    <td>
                                      {row.email ? (
                                        <span className="brevo-pines-email-cell">
                                          <span className="brevo-pines-email-text">{row.email}</span>
                                          <button
                                            type="button"
                                            className="brevo-pines-email-edit-btn"
                                            onClick={() => handlePinEmailEditClick(row)}
                                            disabled={pinAssignSubmitting || pinsDeleting || pinsSubmitting}
                                            aria-label={`Editar email del pin ${row.pin}`}
                                            title="Reasignar email"
                                          >
                                            <i className="bi bi-pencil" aria-hidden="true" />
                                          </button>
                                        </span>
                                      ) : (
                                        '—'
                                      )}
                                    </td>
                                    <td>{row.userName || '—'}</td>
                                    <td>
                                      {pinMetaMaps.statusMap.get(String(row.status_type_id ?? ''))
                                        || (row.status_type_id != null ? row.status_type_id : '—')}
                                    </td>
                                    <td>{row.serial_identify || '—'}</td>
                                    <td>
                                      {pinMetaMaps.timeMap.get(String(row.time_type_id ?? ''))
                                        || (row.time_type_id != null ? row.time_type_id : '—')}
                                    </td>
                                    <td>
                                      {pinMetaMaps.pinTypeMap.get(String(row.pin_type_id ?? ''))
                                        || (row.pin_type_id != null ? row.pin_type_id : '—')}
                                    </td>
                                    <td>{row.userId != null ? row.userId : '—'}</td>
                                    <td>{formatPinTableDate(row.actived_at)}</td>
                                    <td>{formatPinTableDate(row.mpin_created_at)}</td>
                                    {canChangeBrevoList ? (
                                      <td className="brevo-pines-td-actions">
                                        <button
                                          type="button"
                                          className="serials-trash-btn brevo-pines-row-trash"
                                          onClick={() => handleDeleteOnePinRow(row)}
                                          disabled={pinsDeleting || pinsSubmitting}
                                          aria-label={`Eliminar pin ${row.pin}`}
                                          title="Eliminar"
                                        >
                                          <i className="bi bi-trash" aria-hidden="true" />
                                        </button>
                                      </td>
                                    ) : null}
                                  </tr>
                                );
                              })}
                              {!vinculoPines.length ? (
                                <tr>
                                  <td colSpan={canChangeBrevoList ? (isPinesLinkMode ? 13 : 12) : (isPinesLinkMode ? 11 : 10)} className="permission-empty">
                                    No hay pines agregados.
                                  </td>
                                </tr>
                              ) : null}
                              {vinculoPines.length > 0 && sortedFilteredVinculoPines.length === 0 ? (
                                <tr>
                                  <td colSpan={canChangeBrevoList ? (isPinesLinkMode ? 13 : 12) : (isPinesLinkMode ? 11 : 10)} className="permission-empty">
                                    No hay resultados para la búsqueda.
                                  </td>
                                </tr>
                              ) : null}
                            </>
                          )}
                        </tbody>
                      </table>
                    </div>
                    {!pinsLoading ? (
                      <div className="brevo-pines-list-summary" aria-live="polite">
                        Número de pines: <strong>{vinculoPinesStats.total}</strong>
                        <span className="brevo-pines-list-summary-gap" />
                        Usados: <strong>{vinculoPinesStats.used}</strong>
                        <span className="brevo-pines-list-summary-gap" />
                        Libres: <strong>{vinculoPinesStats.free}</strong>
                      </div>
                    ) : null}
                  </div>

                  {canChangeBrevoList ? (
                  <div className="brevo-pines-import-section">
                    {pendingCsvPins.length > 0 ? (
                      <>
                        <div className="brevo-pines-csv-preview">
                          <p className="brevo-pines-csv-preview-count">
                            Número de PINES a importar: <strong>{pendingCsvPins.length}</strong>
                          </p>
                          <p className="brevo-pines-csv-preview-hint">
                            Pulse Importar para guardarlos en el proyecto. Aún no aparecen en la tabla superior.
                          </p>
                          <button
                            type="button"
                            className="brevo-pines-btn-link"
                            onClick={clearPendingCsvPins}
                            disabled={pinsSubmitting}
                          >
                            Volver a entrada manual
                          </button>
                        </div>
                        <div className="brevo-pines-import-row brevo-pines-import-row-csv">
                          <input
                            type="file"
                            accept=".csv"
                            ref={csvFileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleCsvUpload}
                          />
                          <button
                            type="button"
                            className="brevo-pines-btn"
                            onClick={() => csvFileInputRef.current?.click()}
                            disabled={pinsSubmitting}
                          >
                            Otro CSV
                          </button>
                          <button
                            type="button"
                            className="brevo-pines-btn brevo-pines-btn-primary"
                            onClick={handleImportPines}
                            disabled={pinsSubmitting}
                          >
                            {pinsSubmitting ? 'Guardando…' : 'Import'}
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <label className="brevo-pines-import-label" htmlFor="brevo-pines-manual-input">
                          Pines separados por coma:
                        </label>
                        <div className="brevo-pines-import-row">
                          <input
                            id="brevo-pines-manual-input"
                            type="text"
                            className="brevo-pines-import-input"
                            value={pinInput}
                            onChange={(e) => setPinInput(e.target.value)}
                            placeholder="pin1, pin2, pin3..."
                            onKeyDown={(e) => { if (e.key === 'Enter') handleImportPines(); }}
                          />
                          <input
                            type="file"
                            accept=".csv"
                            ref={csvFileInputRef}
                            style={{ display: 'none' }}
                            onChange={handleCsvUpload}
                          />
                          <button
                            type="button"
                            className="brevo-pines-btn"
                            onClick={() => csvFileInputRef.current?.click()}
                            disabled={pinsSubmitting}
                          >
                            CSV
                          </button>
                          <button
                            type="button"
                            className="brevo-pines-btn brevo-pines-btn-primary"
                            onClick={handleImportPines}
                            disabled={pinsSubmitting}
                          >
                            {pinsSubmitting ? 'Guardando…' : 'Import'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                  ) : null}
                </div>
              )}
            </Modal_proyectos>
            {showPinsSyncWaitModal ? (
              <div
                className="project-pins-sync-backdrop"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="project-pins-sync-title"
                aria-live="polite"
              >
                <div className="project-pins-sync-dialog">
                  <h3 id="project-pins-sync-title">
                    {isPinesLinkMode ? 'Sincronización de pines y dashboard' : 'Actualizando estado de los pines'}
                  </h3>
                  <p className="project-pins-sync-hint">Este proceso puede tardar varios minutos. No cierre esta ventana.</p>
                  {isPinesLinkMode && (pinsSyncStep === 'batch' || pinsSyncStep === 'bigquery') ? (
                    <ol className="project-pins-sync-steps">
                      <li
                        className={
                          pinsSyncStep === 'batch'
                            ? 'project-pins-sync-step is-active'
                            : 'project-pins-sync-step is-done'
                        }
                      >
                        <span className="project-pins-sync-step-label">Actualizando estado de los pines</span>
                        <span className="project-pins-sync-step-detail">
                          Consulta por lote (mpins), mismo servicio que Estado de PIN.
                        </span>
                      </li>
                      <li
                        className={
                          pinsSyncStep === 'bigquery'
                            ? 'project-pins-sync-step is-active'
                            : 'project-pins-sync-step is-pending'
                        }
                      >
                        <span className="project-pins-sync-step-label">Actualizando dashboard</span>
                        <span className="project-pins-sync-step-detail">
                          Sincronización del dashboard con los usuarios vinculados.
                        </span>
                      </li>
                    </ol>
                  ) : (
                    <p>
                      Se verificará el estado de cada PIN y el correo electrónico vinculado, usando el mismo servicio que la sección Estado de PIN (consulta por lote).
                    </p>
                  )}
                  <div className="project-pins-sync-spinner" aria-hidden="true" />
                </div>
              </div>
            ) : null}
            {pinAssignModal ? (
              <div
                className="project-pins-sync-backdrop"
                role="dialog"
                aria-modal="true"
                aria-labelledby="pin-assign-title"
              >
                <div className="project-pins-assign-dialog">
                  <h3 id="pin-assign-title">Reasignar PIN</h3>
                  <p className="project-pins-assign-pin">
                    PIN: <strong>{pinAssignModal.pin}</strong>
                  </p>
                  <p className="project-pins-assign-hint">
                    Email actual: <strong>{pinAssignModal.currentEmail}</strong>
                  </p>
                  <label className="project-pins-assign-label" htmlFor="pin-assign-new-email">
                    Nuevo email de destino
                  </label>
                  <input
                    id="pin-assign-new-email"
                    type="email"
                    className="project-pins-assign-input"
                    value={pinAssignNewEmail}
                    onChange={(e) => setPinAssignNewEmail(e.target.value)}
                    disabled={pinAssignSubmitting}
                    autoComplete="off"
                    placeholder="usuario@dominio.com"
                  />
                  <label className="project-pins-assign-label" htmlFor="pin-assign-confirm-email">
                    Confirme el email de destino
                  </label>
                  <input
                    id="pin-assign-confirm-email"
                    type="email"
                    className="project-pins-assign-input"
                    value={pinAssignConfirmEmail}
                    onChange={(e) => setPinAssignConfirmEmail(e.target.value)}
                    disabled={pinAssignSubmitting}
                    autoComplete="off"
                    placeholder="Reescriba el email de destino"
                  />
                  {pinAssignNewEmail.trim() && pinAssignConfirmEmail.trim() && (
                    <p className="project-pins-assign-preview">
                      Se transferirá de <strong>{pinAssignModal.currentEmail}</strong> a{' '}
                      <strong>{pinAssignNewEmail.trim()}</strong>
                    </p>
                  )}
                  <div className="project-pins-assign-actions">
                    <button
                      type="button"
                      className="brevo-pines-btn"
                      onClick={closePinAssignModal}
                      disabled={pinAssignSubmitting}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="brevo-pines-btn brevo-pines-btn-primary"
                      onClick={handleConfirmPinAssign}
                      disabled={
                        pinAssignSubmitting
                        || !pinAssignNewEmail.trim()
                        || pinAssignNewEmail.trim().toLowerCase() !== pinAssignConfirmEmail.trim().toLowerCase()
                      }
                    >
                      {pinAssignSubmitting ? 'Reasignando…' : 'Confirmar reasignación'}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
            </>
          ) : null}
          <Modal_proyectos
            isOpen={isModalPermisos}
            onClose={() => setModalPermisos(false)}
            title="Permisos de Acceso"
          >
            <div className="permission-modal-content">
              <div className="permission-add-card">
                {canManagePermissions ? (
                  <div className="permission-add-form">
                    <div className="permission-field">
                      <label htmlFor="permission-new-email">Email</label>
                      <input
                        type="email"
                        id="permission-new-email"
                        placeholder="Email de Google"
                        value={newEmail}
                        onChange={(e) => setnewMail(e.target.value)}
                      />
                    </div>
                    <div className="permission-add-actions">
                      <button type="button" className="btn-cambiar permission-add-btn" onClick={btAdd_permission}>
                        Agregar
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="permission-users-card">
                <div className="table-permission permission-users-table">
                  <table className="permission-table">
                    <thead>
                      <tr>
                        <th style={{ width: '45%' }}>Nombre</th>
                        <th style={{ width: '55%' }}>Email</th>
                        {canManagePermissions ? <th className="permission-th-actions" aria-label="Acciones" /> : null}
                      </tr>
                    </thead>
                    <tbody>
                      {permissions_users.map((project) => (
                        <tr key={project.id}>
                          <td className="permission-cell-name">
                            <span className="permission-name-with-admin">
                              {canToggleProjectAdmin ? (
                                <button
                                  type="button"
                                  className={
                                    Number(project.permission) === 1
                                      ? 'permission-admin-btn is-admin'
                                      : 'permission-admin-btn'
                                  }
                                  onClick={() => btToggle_project_admin(project)}
                                  aria-label={
                                    Number(project.permission) === 1
                                      ? `Quitar administrador a ${project.email_id}`
                                      : `Hacer administrador a ${project.email_id}`
                                  }
                                  title={
                                    Number(project.permission) === 1
                                      ? 'Quitar administrador'
                                      : 'Hacer administrador'
                                  }
                                >
                                  <i
                                    className={
                                      Number(project.permission) === 1
                                        ? 'bi bi-shield-check'
                                        : 'bi bi-shield-lock'
                                    }
                                    aria-hidden="true"
                                  />
                                </button>
                              ) : Number(project.permission) === 1 ? (
                                <span
                                  className="permission-admin-badge"
                                  title="Administrador del proyecto"
                                  aria-label="Administrador del proyecto"
                                >
                                  <i className="bi bi-shield-check" aria-hidden="true" />
                                </span>
                              ) : null}
                              <span className="permission-name-text">{project.usr_name}</span>
                            </span>
                          </td>
                          <td className="permission-cell-email">{project.email_id}</td>
                          {canManagePermissions ? (
                            <td className="permission-cell-actions">
                              <button
                                type="button"
                                className="permission-trash-btn"
                                onClick={() => btDelete_permission(project.id)}
                                aria-label={`Eliminar permiso de ${project.email_id}`}
                                title="Eliminar"
                              >
                                <i className="bi bi-trash" aria-hidden="true" />
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      ))}
                      {permissions_users.length === 0 ? (
                        <tr>
                          <td colSpan={canManagePermissions ? 3 : 2} className="permission-empty">
                            No hay permisos asignados todavía.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </Modal_proyectos>
        </div>

        {/* Dashboard del proyecto (lab-dashboard embebido) */}
        {nombre_proy && (
          <div className="project-dashboard-embed">
            <iframe
              // key por proyecto: al navegar entre proyectos (p.ej. desde la North Star
              // a TOMi Global) el `src` no cambia (no lleva el id), así que React reusaría
              // el mismo iframe y `onLoad` no volvería a disparar → el dashboard se queda
              // en el proyecto anterior. Con key={id} forzamos el remonte: recarga /auth,
              // rehace el handshake con el nuevo projectId y navega al proyecto correcto.
              key={`${id}-${dashParams}-${dashboardReloadNonce}`}
              title="Dashboard del proyecto"
              width="100%"
              height="1115"
              /* Los parámetros viajan en la URL del iframe, no en el postMessage: la URL
                 sobrevive al remonte, y el handshake se envía en el `onLoad`, así que cada
                 recarga habría que reenviarlo y un fallo se perdería en silencio. El token
                 sí va por postMessage — un token en una URL termina en los logs. */
              src={`${LAB_DASHBOARD_URL}/auth?v=${IFRAME_CACHE_BUST}${dashParams ? `&${dashParams}` : ''}`}
              onLoad={handleDashboardIframeLoad}
              frameBorder="0"
              style={{ border: 0 }}
              allowFullScreen
              sandbox="allow-storage-access-by-user-activation allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox allow-downloads"
            />
          </div>
        )}

        {/* Modal Lista de seriales: vista lista (con búsqueda) o vista agregar */}
        <Modal_proyectos
          isOpen={isModalListaSerialesOpen}
          onClose={closeListaSerialesModal}
          title="Lista de seriales"
        >
          <div className="serial-list-modal-content serials-modal-content">
            {serialListView === 'list' ? (
              <>
                <div className="serials-toolbar-card">
                  <div className="serial-list-toolbar">
                    <input
                      type="text"
                      placeholder="Buscar por serial"
                      value={searchSerial}
                      onChange={(e) => setSearchSerial(e.target.value)}
                      className="serial-search-input"
                    />
                    <input
                      type="text"
                      placeholder="Buscar por sub_cat"
                      value={searchSubCat}
                      onChange={(e) => setSearchSubCat(e.target.value)}
                      className="serial-search-input"
                    />
                    {canAddSerials && selectedSerialIds.length > 0 ? (
                      <button
                        type="button"
                        className="add-cards-btn serial-add-btn"
                        onClick={openIesPickerForSelected}
                      >
                        Cambiar IE ({selectedSerialIds.length})
                      </button>
                    ) : null}
                    {canAddSerials ? (
                      <button
                        type="button"
                        className="add-cards-btn serial-add-btn"
                        onClick={() => setSerialListView('add')}
                      >
                        + Agregar seriales
                      </button>
                    ) : null}
                  </div>
                </div>

                <div className="table-container serials-table-card">
                  <table className="serials-table">
                    <thead>
                      <tr>
                        {canAddSerials ? (
                          <th className="serials-th-check" aria-label="Seleccionar">
                            <input
                              ref={serialSelectAllRef}
                              type="checkbox"
                              checked={
                                filteredSerialList.length > 0 &&
                                filteredSerialList.every((s) => selectedSerialIds.includes(s.id))
                              }
                              onChange={toggleSelectAllFilteredSerials}
                              disabled={filteredSerialList.length === 0}
                            />
                          </th>
                        ) : null}
                        <th>Serial</th>
                        <th>Sub_cat</th>
                        <th>Institución</th>
                        <th>Usuario vinculado</th>
                        <th className="serials-th-actions" aria-label="Acciones" />
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSerialList.map((serial) => (
                        <tr key={serial.id}>
                          {canAddSerials ? (
                            <td className="serials-cell-check">
                              <input
                                type="checkbox"
                                checked={selectedSerialIds.includes(serial.id)}
                                onChange={() => toggleSerialSelected(serial.id)}
                                aria-label={`Seleccionar ${serial.serial}`}
                              />
                            </td>
                          ) : null}
                          <td>{serial.serial}</td>
                          <td>
                            <SerialSubCatCell
                              serial={serial}
                              token={token}
                              projectId={id}
                              canEdit={canAddSerials}
                              onSaved={applySerialSubCat}
                            />
                          </td>
                          <td className="serials-cell-link">
                            <SerialIesCell
                              serial={serial}
                              canEdit={canAddSerials}
                              onRequestPick={openIesPickerForSerial}
                            />
                          </td>
                          <td className="serials-cell-link">
                            <SerialUserLinkCell
                              serial={serial}
                              token={token}
                              projectId={id}
                              canEdit={canAddSerials}
                              onLinked={applySerialLink}
                            />
                          </td>
                          <td className="serials-cell-actions">
                            {canAddSerials ? (
                              <button
                                type="button"
                                className="serials-trash-btn"
                                onClick={() => { btDelete(serial.id); }}
                                aria-label={`Eliminar serial ${serial.serial}`}
                                title="Eliminar"
                              >
                                <i className="bi bi-trash" aria-hidden="true" />
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {filteredSerialList.length === 0 && (
                  <p className="serial-list-empty">{searchSerial || searchSubCat ? 'No hay resultados.' : 'No hay seriales en el proyecto.'}</p>
                )}
              </>
            ) : canAddSerials ? (
              <div className="serial-modal-content">
                <label htmlFor="serial">serial o serial,sub_cat por línea</label>
                <textarea
                  id="serial"
                  rows="8"
                  cols="50"
                  className="serial-modal-textarea"
                  onChange={(e) => countSerials(e.target.value)}
                  ref={serialInputRef}
                />
                <label htmlFor="serial">Seriales por agregarse: {num_seriales}</label>
                <div className="field-container">
                  <label>Procesados #: {num_seriales_procesados}</label>
                </div>
                <div className="serial-result-block">
                  <label>Seriales registrados:</label>
                  <div className="serial-result-list">{ok_serials}</div>
                </div>
                <div className="serial-result-block">
                  <label>Seriales fallidos:</label>
                  <div className="serial-result-list">{failed_serials}</div>
                </div>
                <div className="button-container serial-add-actions">
                  <button className="add-cards-btn" onClick={insertSerialClick}>
                    Agregar seriales al proyecto
                  </button>
                  <button
                    type="button"
                    className="project-header-btn serial-cancel-btn"
                    onClick={() => setSerialListView('list')}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <div className="serial-modal-content">
                <p className="serial-list-empty">No tienes permisos para agregar nuevos seriales.</p>
                <div className="button-container serial-add-actions">
                  <button
                    type="button"
                    className="project-header-btn serial-cancel-btn"
                    onClick={() => setSerialListView('list')}
                  >
                    Volver
                  </button>
                </div>
              </div>
            )}
          </div>
        </Modal_proyectos>

        <IesPickerModal
          isOpen={isIesPickerOpen}
          onClose={closeIesPicker}
          token={token}
          onSelect={handleIesPickerSelect}
          countryIndex={iesCountryIndex}
        />
    </div>
  );
};

export default Project_admin;
